// GreenGlam Lookup — predictive search that renders results INTO the product grid
// (no popup). On query it swaps server-rendered product cards into the grid; an
// emptied query restores the original collection cards.
//
// ── SCOPE CHIPS — state model (epic fttx) ────────────────────────────────────
// ONE source of truth: THE URL. No transient scope state anywhere.
//   • Collection scope = the PATHNAME. Its chip renders server-side on
//     collection pages; removing it is a real state transition — pushState to
//     /collections/all (keeping filter.*/q) + in-place re-render of grid, nav
//     and header from the fetched page. It does not "come back on clear";
//     re-scoping = clicking a category (a real navigation).
//     Search scope rides as filter.p.m.product_data.collection="<title>" — a
//     metafield Shopify syncs nightly to collection membership (DE + EN),
//     locale-safe, exact 1:1 with the collection.
//   • Every other chip is a pure PROJECTION of one filter.* URL param (vendor,
//     product type, concern metafields like "bei Couperose"). Removing one is
//     a URL MUTATION routed to the current grid owner (see below); chips are
//     re-derived from the URL afterwards (#syncChipsFromURL, also fired on the
//     facets' FilterUpdate event) — chips, panel, grid and nav can't disagree.
//   • The QUERY is projected into the URL too (?q=, replaceState) — reload/
//     share always reproduces the visible state (?q= hydration already exists).
//   • Grid ownership is MODAL: empty input → the server/facets render owns the
//     grid; query active → this component owns it via /search fetches. Every
//     mutation goes through exactly ONE writer for the current mode.
//   • The cached original grid is stamped with its URL; clear restores it only
//     if the URL still matches, otherwise the browse grid is fetched fresh.
//
// Search source = the real /search results page ONLY (no predictive suggest.json
// gate). /search is fuzzy + searches the description, so it's the authority on
// what matches; a suggest pre-gate was stricter and produced false negatives
// (typos, ingredient terms in the description). No-match is read straight from the
// fetched grid: /search renders its fallback recommendations OUTSIDE .product-grid,
// so an empty .product-grid = a confirmed no-match. The grid is the gate.
//
// Why a page fetch, not a client filter: the grid only holds the first few loaded
// pages of a large catalog, so filtering visible cards can never surface matches
// deeper in the collection. The fetched page returns the true matches as the same
// .product-grid__item card markup, so the GG card styles apply unchanged.

// ── "Did you mean" condition-term correction ──────────────────────────────────
// Misspelled SKIN-CONDITION queries semantic-broaden into the wrong category (the
// typo "Cuperose" embeds into the perfume cluster → 112 false hits). We can't
// disable semantic search (no API or storefront toggle), so we silently rewrite a
// known misspelling to its CANONICAL spelling BEFORE searching — "Cuperose" fetches
// "Couperose" and returns the 7 right products, which the search-results.liquid
// condition guard then keeps. Word-level + case-insensitive, so multi-word queries
// keep their other words ("cuperose creme" → "couperose creme").
//
// Keep these spelling variants in sync with the `condition_groups` list in
// sections/search-results.liquid (same data, opposite direction).
const GG_CONDITION_CORRECTIONS = {
  cuperose: 'couperose', kuperose: 'couperose', couperos: 'couperose', cooperose: 'couperose',
  rosazea: 'rosacea', rosacia: 'rosacea', rosaca: 'rosacea',
  neurodermitits: 'neurodermitis', neurodermatitis: 'neurodermitis',
  acne: 'akne',
  zellulite: 'cellulite',
  roetungen: 'rötungen', roetung: 'rötung',
  ekzeme: 'ekzem', eczema: 'ekzem',
  // Synonym broadening: "schwanger" matches few products on its own (11), while
  // "schwangerschaft" returns the full set (113) that the search-results guard then
  // narrows to the "Sicher in der Schwangerschaft"-tagged (pregnancy-safe) products.
  schwanger: 'schwangerschaft',
};

function ggCorrectConditionQuery(query) {
  if (!query) return query;
  // LOCALE GATE: every entry above maps to a GERMAN canonical term, and a few
  // (acne→akne, eczema→ekzem) rewrite a correct ENGLISH word into German. Running
  // this on a non-German storefront corrupts valid English queries against the
  // English search index (e.g. "acne" → "akne" → fewer/zero hits). Only correct
  // on the German locale; pass every other locale's query through untouched.
  const locale = ((window.Shopify && window.Shopify.locale) || '').toLowerCase();
  if (!locale.startsWith('de')) return query;
  let changed = false;
  const corrected = query
    .split(/\s+/)
    .map((word) => {
      const fix = GG_CONDITION_CORRECTIONS[word.toLowerCase()];
      if (fix) {
        changed = true;
        return fix;
      }
      return word;
    })
    .join(' ');
  return changed ? corrected : query;
}

// The typeable facet vocabulary. Parsing + ranking live in their own module so
// this file holds no matching logic — the seam that was missing while the
// matcher reached directly into the render pipeline.
import { FilterVocabulary } from '@theme/gg-filter-vocabulary';

class GgLookupComponent extends HTMLElement {
  // sessionStorage key for the Shop Filter panel open state (persisted across
  // collection-switch navigations).
  static #FILTERS_OPEN_KEY = 'gg-shop-filters-open';

  #abortController = null;

  // The pending (queued, not yet fired) search debounce. Held on the instance
  // rather than inside a #debounce closure so a suggestion click can CANCEL it
  // — see #cancelPendingSearch.
  #searchTimer = 0;

  // Cached innerHTML of the untouched collection grid, captured before the first
  // query swap so an emptied query can restore the original cards. STAMPED with
  // the URL it was captured under (#originalGridURL): if the URL has changed
  // since (a filter chip was removed, a facet toggled), the cache is stale and
  // the clear path fetches a fresh browse grid instead of restoring it.
  #originalGridHTML = null;
  #originalGridURL = null;
  // Who owns the grid right now — the THIRD owner is the one that used to be
  // invisible here:
  //   'server' — the server's own render (first paint, browse fetches). Baselines
  //     are only ever captured FROM this mode; a search grid is never a baseline.
  //   'lookup' — this component, rendering from /search fetches.
  //   'facets' — facets-form-component, after we hand a URL to updateFiltersByURL
  //     (a typed filter match, or a chip removal with an empty input).
  // Why 'facets' must be modelled: its render completion is NOT observable —
  // updateFiltersByURL (facets.js) is synchronous and void, discards the section
  // render's promise, and dispatches filter:update BEFORE the request goes out.
  // There is no filter:rendered / section:rendered event anywhere in the theme.
  // So handing the grid to facets is a TERMINAL transition: we clear our own busy
  // state at the handoff, because the facets render is server-authoritative and
  // will repaint regardless. Anything that tried to COUNT that fetch could never
  // decrement — a permanent-busy bug. Ownership, not counting.
  #owner = 'server';
  #resultsList = null;

  // ── Search-results pagination (independent of the paused collection scroll) ──
  // While a query is active the collection's infinite scroll is disconnected, so
  // the swapped-in search results get their OWN paging: a dedicated observer
  // watches a sentinel after the grid and appends /search page N+1 on intersect.
  #searchQuery = '';      // the exact search string currently shown (with scope)
  #searchPage = 1;        // last search page appended
  #searchLastPage = 1;    // data-last-page from the fetched grid (the cap)
  #searchLoading = false;  // guard against overlapping append fetches
  #searchObserver = null;  // IntersectionObserver for the search sentinel
  #searchSentinel = null;  // the watched element

  // Original collection count, captured so an emptied query can restore it.
  // The count ELEMENT is always looked up live (#countEl()) — the collection
  // header can be swapped by #applyServerChrome/morphs, detaching cached refs.
  #originalCount = null;

  #countEl() {
    return document.querySelector('.gg-collection-header__count[data-results-count]');
  }

  // Live lookup of the current product grid <ul>. NOT cached — results-list/morph
  // can swap the node, and a stale ref would write to a detached element.
  #gridEl() {
    return document.querySelector('.product-grid');
  }

  // ── Ownership + busy, as ONE projection ───────────────────────────────────
  // #setOwner is the only writer of #owner, and it projects the busy state with
  // it: 'lookup' with a fetch in flight = busy; every other transition is not.
  // Handing off to 'facets' therefore clears busy by construction (see #owner).
  //
  // The busy CLASS lives on this component, never on the grid or its ancestors:
  // results-list / .collection-wrapper / #ResultsList / .product-grid are all
  // inside the morphed subtree with no skip marker, and morph.js overwrites the
  // whole class attribute — a class set there is wiped by the next facet render.
  // This component carries data-skip-node-update, which is exactly why every
  // other state class (--stuck, --filters-open) already lives here. CSS reaches
  // the grid from here by sibling/descendant selector.
  #busy = false;

  #setOwner(owner, { busy = false } = {}) {
    this.#owner = owner;
    this.#setBusy(owner === 'lookup' && busy);
  }

  // Always re-asserted, never early-returned on an unchanged value: aria-busy
  // lives on .product-grid-container, which IS inside the morphed subtree, so a
  // facet render wipes it out from under us. Deduping against #busy would then
  // leave the attribute permanently missing. The class toggles are idempotent
  // and this runs at most a few times per keystroke, so re-asserting is free.
  #setBusy(busy) {
    this.#busy = busy;
    this.classList.toggle('gg-lookup--loading', busy);
    // Pending (the pre-paint skeleton) is a STRICTER state than loading: it
    // means "nothing on screen is trustworthy yet". It only ever clears.
    if (!busy) this.classList.remove('gg-lookup--pending');
    const container = document.querySelector('.product-grid-container');
    if (container) container.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  // Announce a settled result to the polite live region (the element exists in
  // main-collection.liquid but only #liveNavigate ever wrote to it, so typing
  // and ?q= hydration announced nothing at all).
  #announce(text) {
    const el = document.querySelector('[data-gg-announce]');
    if (el && text) el.textContent = text;
  }
  // ── Scope chips (epic fttx) ──────────────────────────────────────────────
  // Visible chips in [data-gg-chips]; each node carries its own URL constraint.
  // kind: 'collection' | 'vendor' (future: suggested tags). Removal REMOVES the
  // node (after the float-out); chips re-enter when their param returns.
  #chips(kind) {
    // A chip animating out (--out) is already logically removed — it must stop
    // contributing constraints the moment it's clicked, not on animationend.
    const sel = `.gg-lookup__chip:not(.gg-lookup__chip--out)${kind ? `[data-chip-kind="${kind}"]` : ''}`;
    return [...this.querySelectorAll(sel)];
  }

  // Locale-aware Shop root (/collections/all).
  #allUrl() {
    return `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}collections/all`;
  }

  // Current URL minus ONE param=value pair (other values of the same param
  // survive — multi-value facets). Also drops stale paging.
  #urlWithout(param, value) {
    const u = new URL(location.href);
    const rest = [...u.searchParams.entries()].filter(([p, v]) => !(p === param && v === value));
    u.search = '';
    for (const [p, v] of rest) u.searchParams.append(p, v);
    u.searchParams.delete('page');
    return `${u.pathname}${u.search}`;
  }

  // Animated chip exit, then REMOVE the node. Chips are re-created from the
  // URL when their param returns — there is no hidden/restorable chip state.
  #dismissChipNode(chip) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      chip.remove();
      return;
    }
    chip.classList.add('gg-lookup__chip--out');
    chip.addEventListener('animationend', () => chip.remove(), { once: true });
  }

  // ── THE state-param definition (single source — everything that reads or
  // rewrites URL state derives from these two; never inline the checks). ──
  // Filter params = the facet state carried across scopes/navigations.
  static #isFilterParam(param) {
    return param.startsWith('filter.');
  }
  // Non-filter params that are also state and must survive scope transitions.
  static #CARRY_PARAMS = ['q', 'sort_by'];

  // Which URL params project as chips: filter.* except price (numeric min/max),
  // availability (value "1", unreadable) and the collection-scope facet (that
  // one IS the collection chip, owned by the pathname).
  static #isChipParam(param, value) {
    return (
      Boolean(value) &&
      GgLookupComponent.#isFilterParam(param) &&
      !param.startsWith('filter.v.price') &&
      param !== 'filter.v.availability' &&
      param !== 'filter.p.m.product_data.collection'
    );
  }

  // Re-derive the filter/vendor chips from the URL (the collection chip is
  // pathname-owned and untouched here). Idempotent; new chips animate in,
  // vanished ones animate out. Labels: facet values ARE their labels for the
  // list facets we project (metafields, vendor, product type).
  #syncChipsFromURL() {
    const box = this.querySelector('[data-gg-chips]');
    const tpl = box?.querySelector('template[data-gg-chip-template]');
    if (!box || !tpl) return;
    const wanted = [...new URLSearchParams(location.search)].filter(([p, v]) =>
      GgLookupComponent.#isChipParam(p, v)
    );
    for (const chip of box.querySelectorAll('.gg-lookup__chip')) {
      if (chip.dataset.chipKind === 'collection' || chip.classList.contains('gg-lookup__chip--out')) continue;
      if (!wanted.some(([p, v]) => p === chip.dataset.chipParam && v === chip.dataset.chipValue)) {
        this.#dismissChipNode(chip);
      }
    }
    for (const [param, value] of wanted) {
      const exists = [...box.querySelectorAll('.gg-lookup__chip:not(.gg-lookup__chip--out)')].some(
        (c) => c.dataset.chipParam === param && c.dataset.chipValue === value
      );
      if (exists) continue;
      const chip = tpl.content.firstElementChild.cloneNode(true);
      chip.dataset.chipKind = param === 'filter.p.vendor' ? 'vendor' : 'filter';
      chip.dataset.chipParam = param;
      chip.dataset.chipValue = value;
      chip.querySelector('.gg-lookup__chip-label').textContent = value;
      box.insertBefore(chip, tpl);
    }
  }

  // Search-mode URL mutations bypass the section render, so the server-rendered
  // nav echoes must be reconciled by hand: category/subcategory hrefs carry the
  // CURRENT filter.* params (gg-shop-carry-query semantics), and the "(brand)"
  // suffix follows filter.p.vendor. In browse mode the facets section render
  // does all of this server-side instead.
  // After a browse fetch, the fetched document is the server truth for the
  // current URL — sync the category nav (active state, subcats, "(brand)"
  // suffix, carried hrefs) and the collection header (title + count) alongside
  // the grid, so nothing on the page contradicts the URL.
  // Cascade the freshly swapped/appended cards in (CSS does the animating —
  // .product-grid--gg-enter + per-card --gg-i stagger, capped so deep grids
  // don't wait). `from` staggers an appended batch relative to itself.
  #animateGridEntrance(grid, from = 0) {
    if (!grid) return;
    grid.classList.add('product-grid--gg-enter');
    const items = grid.querySelectorAll('.product-grid__item');
    for (let i = from; i < items.length; i++) {
      items[i].style.setProperty('--gg-i', Math.min(i - from, 16));
    }
  }

  // "Chrome" = the page FURNITURE (nav, header, facets, chips) — classic web
  // jargon, NOT the Chrome browser. Everything here is browser-agnostic.
  #applyServerChrome(doc) {
    if (!doc) return;
    const swap = (sel) => {
      const next = doc.querySelector(sel);
      const live = document.querySelector(sel);
      if (next && live) live.innerHTML = next.innerHTML;
    };
    // The header swap re-renders the Filter toggle (server default:
    // aria-expanded="false") and the grid-size radios (server default: Normal
    // checked) — capture client state first, re-assert after. This is THE choke
    // point: every live nav and browse render funnels through here.
    const gridVal = document.querySelector('.gg-grid-size__input:checked')?.value;
    swap('.gg-shop-nav__cats-wrap'); // category nav: active state, subcats, carried hrefs
    swap('.gg-collection-header'); // title + count
    swap('.gg-shop-nav__filter'); // facets panel: THIS scope's values/counts (custom elements re-upgrade on insertion)
    this.#reconcileChipsFromDoc(doc); // chips: server truth applied WITHOUT re-inserting survivors
    this.#parseFilterMap(); // the map template rode in with the cats-wrap swap — new scope, new keywords
    this.#restartTypewriter(); // ditto for the prompts template — new scope, new prompts
    this.#syncFilterPanel(this.classList.contains('gg-lookup--filters-open'));
    if (gridVal) {
      document.querySelectorAll('.gg-grid-size__input').forEach((input) => {
        input.checked = input.value === gridVal;
      });
    }
  }

  // Server truth for the chip box WITHOUT innerHTML-blasting it: a blunt swap
  // re-inserts (and re-animates) chips already on screen — the "chip appears,
  // then flashes through its entrance again" bug on category clicks
  // (#patchCollectionChip pre-patches a chip, the swap then replayed gg-float-in
  // on the fresh server node). Reconcile instead: the collection chip matches by
  // KIND (its param flips between product_data.collection and filter.p.vendor
  // for brand collections), facet chips by param+value. Matches are updated in
  // place (no animation); vanished chips float out; only genuinely new server
  // chips are inserted and float in.
  #reconcileChipsFromDoc(doc) {
    const box = this.querySelector('[data-gg-chips]');
    const nextBox = doc.querySelector('[data-gg-chips]');
    if (!box || !nextBox) return;
    const wanted = [...nextBox.querySelectorAll('.gg-lookup__chip')];
    const live = [...box.querySelectorAll('.gg-lookup__chip:not(.gg-lookup__chip--out)')];
    const matchOf = (chip) =>
      wanted.find((w) =>
        chip.dataset.chipKind === 'collection'
          ? w.dataset.chipKind === 'collection'
          : w.dataset.chipKind !== 'collection' &&
            w.dataset.chipParam === chip.dataset.chipParam &&
            w.dataset.chipValue === chip.dataset.chipValue
      );
    const matched = new Set();
    for (const chip of live) {
      const w = matchOf(chip);
      if (!w) {
        this.#dismissChipNode(chip);
        continue;
      }
      matched.add(w);
      chip.dataset.chipKind = w.dataset.chipKind;
      chip.dataset.chipParam = w.dataset.chipParam;
      chip.dataset.chipValue = w.dataset.chipValue;
      const label = chip.querySelector('.gg-lookup__chip-label');
      const nextLabel = w.querySelector('.gg-lookup__chip-label');
      if (label && nextLabel && label.textContent !== nextLabel.textContent) {
        label.textContent = nextLabel.textContent;
      }
    }
    const tpl = box.querySelector('template[data-gg-chip-template]');
    for (const w of wanted) {
      if (matched.has(w)) continue;
      const chip = document.importNode(w, true);
      if (tpl) box.insertBefore(chip, tpl);
      else box.appendChild(chip);
    }
  }

  // ── LIVE scope navigation (Phase B) ───────────────────────────────────────
  // Category clicks become pushState + in-place re-render — the Shop's
  // superpower (live grid) extended to navigation; zero page loads inside the
  // Shop. Real hrefs stay in the DOM (SEO/crawlers/middle-click untouched).
  // Bails out to a REAL navigation for: theme editor (the customizer drives
  // its preview through real loads), modified clicks (new tab etc.), other
  // origins, Marken (different template), and any render failure — a live
  // transition must never strand the user.
  async #liveNavigate(url, event, linkText) {
    if (window.Shopify && window.Shopify.designMode) return;
    if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)) return;
    const u = new URL(url, location.origin);
    if (u.origin !== location.origin || /\/collections\/marken\/?$/.test(u.pathname)) return;
    event?.preventDefault();

    // Close any open hover subrow before the swap. #applyServerChrome replaces
    // .gg-shop-nav__cats-wrap's innerHTML, so the open panel's node is destroyed
    // and rebuilt from server markup mid-transition; closing first means the
    // panel is never left mid-animation across a node it no longer owns.
    // Instant, not animated — the node it would animate is about to be replaced.
    // Same hook the suggestion row uses when it takes over the strip.
    this.#closeSubrows?.();

    const q = this.input.value.trim();
    if (q) u.searchParams.set('q', q);
    else u.searchParams.delete('q');
    const target = `${u.pathname}${u.search}`;
    history.pushState(history.state, '', target);

    // Instant chip feedback pre-fetch; the browse step of the transition
    // overwrites it with server truth.
    this.#patchCollectionChip(u, linkText);

    const ok = await this.#transitionToURL(q);
    if (ok === false) {
      location.href = target; // graceful degradation to a real navigation
      return;
    }

    // Orient + announce like a navigation would — unless the lookup is STUCK:
    // then the user is browsing mid-grid and a scope change should read like a
    // filter change, not a page load. Land the fresh grid's start (collection
    // header) right under the pinned bar instead of ripping back to the hero;
    // the bar never unsticks, the new results are visible from row 1, and a
    // shorter collection can't strand them past its end.
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const header = document.querySelector('.gg-collection-header');
    if (this.classList.contains('gg-lookup--stuck') && header) {
      const y =
        window.scrollY + header.getBoundingClientRect().top - this.getBoundingClientRect().bottom - 12;
      window.scrollTo({ top: Math.max(0, y), behavior });
    } else {
      window.scrollTo({ top: 0, behavior });
    }
    const announce = document.querySelector('[data-gg-announce]');
    if (announce) {
      announce.textContent = document.querySelector('.gg-collection-header')?.textContent.replace(/\s+/g, ' ').trim() || '';
    }

  }

  // Make the collection chip reflect a pathname we just live-navigated to:
  // /collections/all → no chip; a collection → chip labeled with the clicked
  // link's title (identical to collection.title, which is the facet value).
  #patchCollectionChip(u, linkText) {
    const box = this.querySelector('[data-gg-chips]');
    const tpl = box?.querySelector('template[data-gg-chip-template]');
    let chip = box?.querySelector('.gg-lookup__chip[data-chip-kind="collection"]');
    if (/\/collections\/all\/?$/.test(u.pathname)) {
      chip?.remove();
      return;
    }
    if (!linkText || !box || !tpl) return;
    if (!chip) {
      chip = tpl.content.firstElementChild.cloneNode(true);
      chip.dataset.chipKind = 'collection';
      chip.dataset.chipParam = 'filter.p.m.product_data.collection';
      box.insertBefore(chip, tpl);
    }
    chip.dataset.chipValue = linkText;
    const label = chip.querySelector('.gg-lookup__chip-label');
    if (label) label.textContent = linkText;
  }

  // THE one scope-transition path: re-derive EVERYTHING from the (already
  // updated) URL.
  //   • No query → one browse render (grid + chrome + chips = server truth).
  //   • Query active → the GRID swaps exactly once, straight to the new
  //     results (no "all products" flash): the search render and a parallel
  //     chrome-only fetch of the browse doc run concurrently — nav highlight,
  //     subcats, header, facets and chips are scope-bound (not query-bound),
  //     so applying them independently is safe. The chrome fetch stays outside
  //     the abort chain: even if a newer keystroke supersedes the search, the
  //     chrome is still correct for the URL. Failure = stale chrome until the
  //     next transition (minor) — the search result itself still decides
  //     success/fallback.
  // chromeFirst: history navigations (back/forward) can't pre-patch the
  // collection chip from a clicked link — the chip must come from the server
  // BEFORE the search runs, or the search scopes to the collection being LEFT
  // (QA test 10: correct furniture over wrong results). Click-driven
  // transitions keep the faster parallel path (their chip is already patched).
  async #transitionToURL(q, chromeFirst = false) {
    if (!q) return this.#renderResults('', true);
    const chromeHTML = fetch(this.#browseUrl().toString())
      .then((r) => (r.ok ? r.text() : null))
      .catch(() => null);
    if (chromeFirst) {
      const html = await chromeHTML;
      if (!html) {
        // Can't establish the popped scope's truth → a real load is the only
        // honest fallback (never search under a lying scope).
        location.reload();
        return false;
      }
      this.#applyServerChrome(new DOMParser().parseFromString(html, 'text/html'));
      return this.#renderResults(q); // chips are server-true; count/echo set by the search itself
    }
    const ok = await this.#renderResults(q);
    // Chrome applies LAST (deterministic): the header swap brings the scope's
    // server count, so the search's count + query echo re-assert afterwards.
    const html = await chromeHTML;
    if (html) {
      const searchCount = this.#countEl()?.textContent;
      this.#applyServerChrome(new DOMParser().parseFromString(html, 'text/html'));
      if (ok !== false && this.#owner === 'lookup') {
        const el = this.#countEl();
        if (el && searchCount != null) el.textContent = searchCount;
        this.#setQueryEcho(this.input.value.trim());
      }
    }
    return ok;
  }

  // Back/forward + bfcache: the URL changed under us — same re-derivation,
  // but chrome-FIRST (see #transitionToURL): the popped scope's chips must be
  // server-true before any search runs.
  #onHistoryNav = () => {
    if (!this.#gridEl()) return;
    const q = (new URLSearchParams(location.search).get('q') || '').trim();
    this.input.value = q;
    this.#syncChipsFromURL();
    this.#transitionToURL(q, true);
  };

  // ── No-match presentation (Phase A) ───────────────────────────────────────
  // A managed sibling ABOVE the grid: honest "Keine Ergebnisse" message, plus a
  // clearly-labelled suggestions heading when fallback products are shown
  // (decision: suggestions read better psychologically, but must never read as
  // results). Removed entirely on real matches / restore.
  //
  // LEADS WITH FILTERS when the query names facet values. A zero-result search
  // used to convert to a filter SILENTLY (auto-tagging's last branch): the
  // shopper asked for "Glas" and simply got a filtered grid, never told the
  // search itself had found nothing. Deleting that in step 5 removed the net as
  // well as the deception, and these are exactly the queries that need it —
  // Shopify's index cannot read metafields, so for a facet value free-text
  // search can NEVER succeed and the filter is the only route to those products.
  // The answer is offered, at the top, as one click. Same tags and same handler
  // as the typing row; the shopper still decides.
  #setResultNote(kind, query, doc) {
    // The query is remembered so focusout can add the lead-in later: it is
    // suppressed while the typing row is up (same tags twice) and has to appear
    // when the row goes down. Only the LEAD-IN is rebuilt then, never the whole
    // note — the rest can contain the server's own empty state, lifted from a
    // `doc` that no longer exists by the time focusout runs.
    this.#lastNoteQuery = kind === 'match' ? null : query;
    document.querySelector('.gg-lookup__result-note')?.remove();
    const grid = this.#gridEl();
    if (kind === 'match' || !grid) return;
    const note = document.createElement('div');
    note.className = 'gg-lookup__result-note';
    if (kind === 'empty') {
      // Lift the server's honest empty state (condition guard / zero-result
      // filter) when the fetched doc carries one — server truth, no duplication.
      const lifted = doc?.querySelector('.main-collection-grid__empty');
      if (lifted) note.innerHTML = lifted.outerHTML;
    }
    if (!note.innerHTML) {
      const msg = document.createElement('p');
      msg.className = 'gg-lookup__result-note-msg';
      msg.textContent = (this.dataset.noresultsText || '').replace('@@Q@@', query || '');
      note.append(msg);
    }
    if (kind === 'fallback') {
      const heading = document.createElement('h4');
      heading.className = 'main-collection-grid__title';
      heading.textContent = this.dataset.suggestionsLabel || '';
      note.append(heading);
    }
    // PREPENDED, and built last: the lead-in leads, but the `!note.innerHTML`
    // test above must see the note as the empty-state logic left it. Adding the
    // lead first made that test always false, so the "no results" message never
    // rendered at all.
    this.#prependFilterLead(note, query);
    grid.before(note);
  }

  // Query echo beside the header count („creme“) — context for the number.
  #setQueryEcho(query) {
    const el = document.querySelector('[data-results-query]');
    if (el) el.textContent = query ? ` · „${query}“` : '';
  }

  #reconcileNavToURL() {
    const current = [...new URLSearchParams(location.search)].filter(([p]) => GgLookupComponent.#isFilterParam(p));
    document.querySelectorAll('.gg-shop-nav__cat[href], .gg-shop-nav__subcat[href]').forEach((a) => {
      const u = new URL(a.getAttribute('href'), location.origin);
      const kept = [...u.searchParams.entries()].filter(([p]) => !GgLookupComponent.#isFilterParam(p));
      u.search = '';
      for (const [p, v] of [...kept, ...current]) u.searchParams.append(p, v);
      a.setAttribute('href', `${u.pathname}${u.search}`);
    });
    if (!current.some(([p]) => p === 'filter.p.vendor')) {
      // data-brand-collection = suffix owned by the PATHNAME (brand collection),
      // not by a vendor param — exempt it from the param-based cleanup.
      document.querySelectorAll('.gg-shop-nav__cat-vendor:not([data-brand-collection])').forEach((el) => el.remove());
    }
  }

  // Base URL for BROWSING (no free-text query) = the current URL itself minus
  // query/paging. The URL is the state, so the browse grid is simply "this
  // page without a search" — pathname carries the collection scope, filter.*
  // params carry the chips. (After a collection-chip removal the pathname is
  // already /collections/all via pushState.)
  #browseUrl() {
    const url = new URL(location.href);
    url.searchParams.delete('q');
    url.searchParams.delete('page');
    return url;
  }

  // ── Typeable filters (epic: concern-filter search, widened in sf87) ─────────
  // Shopify's search index can't read metafields, so filter values (e.g.
  // product_attribute.solution="bei Couperose") are unsearchable by typing.
  // main-collection.liquid emits a keyword→facet map on a <template> inside the
  // nav; typing RANKS it into suggestions and a click applies the FILTER —
  // authoritative, locale-safe, and engine-independent (works on the broken
  // shopify_search locale too). #collectionUrl is the locale-aware fetch base.
  //
  // Rebuilt by #parseFilterMap on every chrome swap: the template rides in with
  // the cats-wrap innerHTML and each collection has its own facet values. Empty
  // on Marken, which emits no template — that makes every suggestion path a
  // silent no-op rather than an error.
  #vocabulary = FilterVocabulary.parse(null);

  #collectionUrl = '/collections/all';

  // ── Rotating typewriter placeholder ──────────────────────────────────────
  // Cycles incentivizing prompts (data-search-prompts, locale-driven, "|"-split)
  // through the input's placeholder. Paused while the field is focused or has a
  // value; fully disabled under prefers-reduced-motion (native placeholder stays).
  #twTimer = null;
  #twPrompts = [];
  #twIndex = 0;
  #twStaticPlaceholder = '';

  connectedCallback() {
    this.input = this.querySelector('.gg-lookup__input');

    if (!this.input) return;

    // NOTE: do NOT cache the grid node here. results-list/morph can replace the
    // .product-grid <ul>, leaving a cached reference detached — writing to it
    // then empties an off-DOM node while the live grid keeps showing everything
    // (this caused "nonsense still shows the whole roster"). Always read it live
    // via #gridEl at swap time.
    // The collection grid lives inside <results-list>, which runs infinite scroll.
    // We pause its IntersectionObserver while a query is active so it can't append
    // more collection pages on top of (or fight) our swapped-in search results.
    this.#resultsList = document.querySelector('results-list');

    // Chip removal — every path is a URL change routed to ONE writer:
    //   collection chip → pushState to /collections/all (+ kept params), then
    //     re-render grid/nav/header in place from the fetched page.
    //   filter/vendor chip, empty input → facets machinery (updateFiltersByURL:
    //     pushState + FilterUpdate + server section render — grid, panel,
    //     category hrefs all come back server-true; our FilterUpdate listener
    //     re-syncs the chips).
    //   filter/vendor chip, query active → this component owns the grid:
    //     replaceState the mutated URL, uncheck the matching panel input,
    //     reconcile nav echoes, re-run the search with fewer constraints.
    this.querySelector('[data-gg-chips]')?.addEventListener('click', (e) => {
      const chip = e.target.closest('.gg-lookup__chip');
      if (!chip || chip.classList.contains('gg-lookup__chip--out')) return;
      const q = this.input.value.trim();
      this.#dismissChipNode(chip); // visual exit; state changes below

      if (chip.dataset.chipKind === 'collection') {
        const u = new URL(this.#allUrl(), location.origin);
        for (const [p, v] of new URLSearchParams(location.search)) {
          if (GgLookupComponent.#isFilterParam(p) || GgLookupComponent.#CARRY_PARAMS.includes(p)) {
            u.searchParams.append(p, v);
          }
        }
        history.pushState(history.state, '', `${u.pathname}${u.search}`);
        this.#transitionToURL(q); // full chrome re-derivation, then the query
        return;
      }

      const url = this.#urlWithout(chip.dataset.chipParam, chip.dataset.chipValue);
      const facetsForm = document.querySelector('facets-form-component');
      if (!q && facetsForm) {
        this.#setOwner('facets'); // terminal handoff — see #owner
        facetsForm.updateFiltersByURL(url);
        return;
      }
      history.replaceState(history.state, '', url);
      facetsForm?.querySelectorAll(`input[name="${CSS.escape(chip.dataset.chipParam)}"]`).forEach((input) => {
        if (input.value === chip.dataset.chipValue) input.checked = false;
      });
      this.#reconcileNavToURL();
      this.#renderResults(q, q === '');
    });

    // Facet changes from the Filter panel (or any FilterUpdate source): the
    // section render refreshes grid/panel/nav server-side, but the chips live
    // in the morph-skipped search subtree — re-derive them from the new URL.
    // (Literal event name: ThemeEvents.FilterUpdate in events.js — this file
    // is not a module, so it can't import the constant.)
    document.addEventListener('filter:update', () => {
      this.#syncChipsFromURL();
      // Release the pre-paint skeleton: we handed the grid to facets, and this
      // is the only signal the theme emits on that path (the section render's
      // completion is not observable — see #owner). It fires just before the
      // request goes out, so the skeleton covers the handoff and the incoming
      // server render lands on a clean grid rather than on hidden cells.
      this.#setBusy(false);
      const q = this.input.value.trim();
      if (q) this.#renderResults(q);
    });
    this.#syncChipsFromURL();

    // Back/forward + bfcache restore (pageshow persisted — Safari's bfcache is
    // aggressive; this keeps restored pages true to their URL).
    window.addEventListener('popstate', this.#onHistoryNav);
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.#onHistoryNav();
    });

    // Build the typeable filter map (re-built after every #applyServerChrome —
    // the source template rides inside the swapped nav, so the map always
    // matches the current scope).
    this.#parseFilterMap();
    this.#collectionUrl =
      this.dataset.collectionUrl ||
      `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}collections/all`;

    // Same 300ms debounce as before, but with the timer on the instance so a
    // suggestion click can cancel a search that is queued and has not fired.
    // Aborting #abortController only stops a fetch already in flight; a search
    // still sitting in this timer would start AFTER the click and repaint the
    // grid the filter just claimed.
    this.input.addEventListener('input', (e) => {
      clearTimeout(this.#searchTimer);
      this.#searchTimer = setTimeout(() => this.#onInput(e), 300);
    });

    // Suggestions run UNDEBOUNCED, deliberately on their own listener: the grid
    // fetch is worth waiting 300ms for, the tag list is not (it is in-memory).
    // Separate listeners keep the two cadences independent instead of coupling
    // the cheap one to the expensive one's timer.
    this.input.addEventListener('input', () => {
      this.#renderSuggestions(this.input.value.trim());
    });
    // Coming BACK to a field that still holds a query: the answer to that query
    // is still the answer, so the row returns without waiting for a keystroke.
    // Without this, clicking back into a field reading "rot" showed nothing
    // until a character was typed — the text was there but its suggestions were
    // not, which reads as the system having forgotten.
    //
    // Still gated on CONTENT, not focus: an empty field renders nothing
    // (#renderSuggestions clears on zero matches) and the categories stay
    // hoverable, which is the invariant #suggestionsGated depends on.
    // focusin, not focus, because it bubbles — same reason as focusout below.
    this.addEventListener('focusin', (e) => {
      // Only the input reopens the row. Clicking a chip or a suggestion also
      // fires focusin on the component, and neither should bring it back.
      if (e.target !== this.input) return;
      const q = this.input.value.trim();
      if (q) this.#renderSuggestions(q);
    });

    // Leaving the field puts the categories back. focusout (not blur) because it
    // bubbles, and relatedTarget lets a click on a suggestion settle first.
    this.addEventListener('focusout', (e) => {
      if (this.contains(e.relatedTarget)) return;
      // Animated ONLY here: leaving is a departure, so the row fades out under
      // the pills returning. Every other caller closes instantly (see
      // #clearSuggestions) — a click is a commit, not a departure.
      this.#clearSuggestions({ animate: true });
      // The row was suppressing the no-match lead-in (same tags twice). Now that
      // it is down, re-paint the note so the lead-in takes over — otherwise a
      // shopper who blurs after a no-match is left with neither.
      this.#repaintResultNote();
    });

    // ── Keyboard: ↑/↓ highlight, Enter applies, Escape dismisses ────────────
    // Enter is NOT bound here. It stays with the form's submit handler (below),
    // which checks #activeIndex: a highlighted tag applies, no highlight
    // searches. Binding Enter in both places would have them race.
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!this.#suggestionsGated()) return; // no row up: let the caret move
        e.preventDefault(); // otherwise the caret jumps to the end of the text
        this.#moveActive(e.key === 'ArrowDown' ? 1 : -1);
        return;
      }
      if (e.key === 'Escape') {
        // Two-stage, so Escape never destroys more than the shopper meant:
        // a highlight is dropped first, and only a second press puts the row
        // away. Matches how the browser's own autocomplete behaves.
        if (this.#activeIndex >= 0) {
          e.preventDefault();
          this.#setActive(-1);
          return;
        }
        if (this.#suggestionsGated()) {
          e.preventDefault();
          this.#clearSuggestions();
        }
      }
    });

    // Delegated on the box (rebuilt wholesale on every re-rank, so per-node
    // listeners would be re-attached on each keystroke).
    // pointerdown, not click, and preventDefault to keep focus in the input.
    // The tags are tabindex="-1" (combobox pattern), and Safari does not focus a
    // button on mousedown — so focus would leave the component, `focusout` would
    // fire with a null relatedTarget, and #clearSuggestions would destroy the tag
    // before its click ever landed. Committing on pointerdown removes the
    // dependency on focus ordering entirely, and covers touch and pen with it.
    this.#suggestionsBox()?.addEventListener('pointerdown', (e) => {
      const tag = e.target.closest('[data-gg-suggestion]');
      if (!tag) return;
      e.preventDefault(); // no focus change → no focusout race
      this.#onSuggestionClick(tag);
    });

    // A pointer entering the row drops the keyboard highlight. Without this the
    // hovered tag and the highlighted tag are two different tags both looking
    // chosen, and Enter would apply the one the pointer is NOT on. The pointer
    // is the newer intent, so it wins; ↓/↑ takes it back.
    this.#suggestionsBox()?.addEventListener('pointerover', (e) => {
      if (this.#activeIndex >= 0 && e.target.closest('[data-gg-suggestion]')) this.#setActive(-1);
    });

    // Keep Enter inside the in-page lookup — block the form's native GET to
    // /search so users stay in the Shop grid. Run the in-page search immediately
    // (no debounce) on submit so Enter feels responsive.
    // On a grid-less page (e.g. the Marken directory) there's nothing to swap
    // into, so do NOT hijack — let the form submit natively to /search.
    this.querySelector('.gg-lookup__form')?.addEventListener('submit', (e) => {
      if (!this.#gridEl()) return;
      e.preventDefault();
      // A HIGHLIGHTED tag wins: the shopper pressed ↓/↑ to aim at it, so Enter
      // means "this one". With no highlight (#activeIndex -1, the resting
      // state) Enter keeps its old meaning — plain typing plus Enter still just
      // searches, and no filter is ever applied without a deliberate aim.
      const active = this.#activeIndex >= 0 ? this.#suggestionTags()[this.#activeIndex] : null;
      if (active) {
        this.#onSuggestionClick(active);
        return;
      }
      // Enter used to also mean "this word is finished", the signal that let an
      // extendable keyword ("bronze", which continues into "bronzer") auto-apply
      // instead of waiting. Nothing auto-applies now, so Enter is simply "search
      // this, don't wait": clear the queued debounce so the immediate search
      // cannot be followed by its own duplicate 300ms later.
      clearTimeout(this.#searchTimer);
      this.#renderResults(this.input.value.trim());
    });

    // ── Editorial nav (bean sed4) ──────────────────────────────────────
    // Category click — visual active toggle; stubs prevent scroll-to-top.
    // New Shop page uses .gg-shop-nav__kategorien/.gg-shop-nav__cat; the legacy
    // gg-lookup section (still used by the Marken template) uses
    // .gg-lookup__pills/.gg-lookup__pill — support both.
    // Bind to .gg-shop-nav__cats-wrap (STABLE — #applyServerChrome swaps its
    // innerHTML, replacing the .gg-shop-nav__kategorien nav inside; a listener
    // on the nav itself would die after the first live transition).
    (this.querySelector('.gg-shop-nav__cats-wrap') ||
      this.querySelector('.gg-shop-nav__kategorien') ||
      this.closest('.gg-lookup')?.querySelector('.gg-lookup__pills'))?.addEventListener(
      'click',
      this.#onCategoryClick.bind(this)
    );

    // Marken's divider still carries a Suche tab (focus shortcut); the Shop
    // renders no divider anymore (bean wuhq — tabs died with the docked card).
    this.querySelector('.gg-shop-nav__divider')
      ?.addEventListener('click', this.#onTabClick.bind(this));

    // Restore the Filter panel open/closed state across collection switches
    // (each category link is a full navigation that would otherwise reset it).
    // GATE on the external panel itself: only the Shop renders #gg-filter-panel
    // (Marken has no filters) — a persisted open state can't leak there.
    // An inline pre-paint script in main-collection.liquid may already have added
    // the open classes (to avoid a closed→open shift); in that case
    // #setFiltersOpen no-ops, so sync the projections explicitly here.
    if (document.getElementById('gg-filter-panel')) {
      const stored = sessionStorage.getItem(GgLookupComponent.#FILTERS_OPEN_KEY) === '1';
      if (stored) this.#setFiltersOpen(true);
      this.#syncFilterPanel(stored);

      // Header "Filter" toggle (inline layout). Document-level delegation: the
      // button lives inside .gg-collection-header, whose innerHTML
      // #applyServerChrome swaps on every live nav — a direct binding would die
      // after the first transition. Shop-only (Marken renders no toggle).
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-gg-filter-toggle]')) return;
        this.#setFiltersOpen(!this.classList.contains('gg-lookup--filters-open'));
      });
    }

    // Hydrate from ?q= — a real /search visit redirects here (search-results.liquid)
    // with the query in the URL. Pre-fill the field and run the in-page search so
    // the user lands inside the Shop GG Lookup system with results already showing.
    // Only on a page that actually has a grid to render into (Shop, /search) —
    // skip the render on grid-less pages (e.g. Marken).
    // ADOPT any pre-paint state: this script is a module, so it runs only after
    // the parser has finished the document — the inline pre-paint script in
    // main-collection.liquid may already have put us in --pending (skeleton
    // showing, server grid hidden) long before now. Start from what the DOM
    // actually says, or #busy would read false while the skeleton is up and the
    // first #setBusy(false) would no-op, stranding it forever.
    this.#busy = this.classList.contains('gg-lookup--pending');

    const urlQuery = new URLSearchParams(location.search).get('q');
    if (urlQuery && urlQuery.trim()) {
      this.input.value = urlQuery.trim();
      if (this.#gridEl()) this.#renderResults(urlQuery.trim());
      else this.#setBusy(false); // no grid to render into — never strand the skeleton
    } else {
      this.#setBusy(false); // pending without a query (stale bfcache) — clear it
    }

    // Kick off the rotating placeholder (no-op if no prompts / reduced motion /
    // the field already carries a hydrated ?q= value).
    this.#startTypewriter();

    // Sticky stuck-state (Shop only — the sentinel is Shop markup).
    this.#initSticky();

    // Subcat hover-intent (Shop only — Marken has no cats-wrap/tag groups).
    this.#initSubrowHover();
  }

  // Hover-intent for the subcat overlays: pure CSS :hover closed the row the
  // instant the pointer crossed the gap between two main tags — visible
  // flashing. JS owns the pointer-open state (`gg-shop-tag-group--open`):
  // opening is immediate; closing waits a grace period so gap crossings never
  // flicker; entering another group hands over instantly. Keyboard stays pure
  // CSS (:focus-within). Bound to .gg-shop-nav__cats-wrap — stable across
  // #applyServerChrome innerHTML swaps (swapped-in markup arrives closed,
  // which is the correct resting state).
  #subrowCloseTimer = 0;

  // Set by #initSubrowHover (which owns the close handshake); called when the
  // suggestion row takes over the strip. Null when hover is not wired at all
  // (touch, or no cats-wrap) — nothing to close there.
  #closeSubrows = null;

  #initSubrowHover() {
    const wrap = this.querySelector('.gg-shop-nav__cats-wrap');
    if (!wrap || !window.matchMedia('(hover: hover)').matches) return;
    // Closing is a two-step handshake so it can ANIMATE (display can't):
    // --open → --closing (CSS wipes the panel shut) → classes cleared on
    // animationend, with a timeout backstop (animationend is lost if the tab
    // hides or reduced-motion kills the animation).
    //
    // EVERY pending close is tracked per group and CANCELLED on re-open.
    // Without that, a mid-close re-entry left the old animationend listener +
    // backstop timer armed; the reveal's own animationend (or the 400ms
    // timer) then stripped --open from the freshly opened panel — it flashed
    // away right after opening, and a click aimed at a subcategory landed on
    // nothing (the "hover doesn't load" / "click twice" bugs).
    const pendingClose = new WeakMap();
    const cancelClose = (group) => {
      const p = pendingClose.get(group);
      if (!p) return;
      clearTimeout(p.timer);
      p.subrow?.removeEventListener('animationend', p.onEnd);
      pendingClose.delete(group);
    };
    const finishClose = (group) => {
      cancelClose(group);
      group.classList.remove(
        'gg-shop-tag-group--open',
        'gg-shop-tag-group--closing',
        'gg-shop-tag-group--handover'
      );
    };
    const closeOpen = (instant = false) => {
      wrap.querySelectorAll('.gg-shop-tag-group--open').forEach((group) => {
        if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          finishClose(group);
          return;
        }
        // --handover must go BEFORE --closing lands: its animation:none rule
        // sits later in the cascade and would kill the conceal wipe.
        group.classList.remove('gg-shop-tag-group--open', 'gg-shop-tag-group--handover');
        group.classList.add('gg-shop-tag-group--closing');
        const subrow = group.querySelector('.gg-shop-tag-subrow');
        // animationend BUBBLES — a sub tag's float-in end must not finish the
        // close early, so only the subrow's own conceal wipe counts (the
        // backstop timer calls onEnd with no event).
        const onEnd = (e) => {
          if (e && e.target !== subrow) return;
          finishClose(group);
        };
        const timer = setTimeout(onEnd, 400);
        subrow?.addEventListener('animationend', onEnd);
        pendingClose.set(group, { subrow, onEnd, timer });
      });
    };
    // GATE — the suggestion row and the category subrow occupy the SAME strip
    // and both animate it, so they are mutually exclusive: while the row is up
    // hover-intent is disabled outright (#suggestionsGated, checked in both
    // pointer handlers) rather than layered. Without it, moving the pointer
    // across the categories on the way to a suggestion opens a subrow beneath
    // the row and two reveal/conceal wipes run on the same strip.
    //
    // #renderSuggestions calls this once, as the row goes up, to clear anything
    // already open. Keyed on the ROW rather than on focus because focus with an
    // empty field shows no suggestions and the categories must stay hoverable
    // then. Gating in JS (not CSS) also stops the timers, so nothing is left
    // armed to fire underneath. No un-gate hook is needed: #suggestionsGated is
    // a live query, so the next pointerover works again once the row is down.
    this.#closeSubrows = () => {
      clearTimeout(this.#subrowCloseTimer);
      closeOpen(true); // instant — there is no room to animate a close under the row
    };

    wrap.addEventListener('pointerover', (e) => {
      if (this.#suggestionsGated()) return;
      const group = e.target.closest('.gg-shop-tag-group');
      if (!group) return;
      clearTimeout(this.#subrowCloseTimer);
      // Re-entering a group mid-close cancels the wipe AND its pending
      // finish (listener + backstop).
      const wasClosing = group.classList.contains('gg-shop-tag-group--closing');
      cancelClose(group);
      group.classList.remove('gg-shop-tag-group--closing');
      if (group.classList.contains('gg-shop-tag-group--open')) return;
      // HANDOVER (no reveal wipe) in two cases:
      // • moving from one open category to another — the panel is
      //   conceptually already open (old closes instantly, new skips the
      //   reveal); only the tags float in again to carry the change;
      // • RESCUING this group mid-close — replaying the reveal from zero
      //   here made the panel visibly blink out and back right as the user
      //   aimed at a subcategory; --handover snaps it back fully visible.
      // Opening from nothing = full reveal.
      const handover = wasClosing || Boolean(wrap.querySelector('.gg-shop-tag-group--open'));
      closeOpen(handover);
      group.classList.toggle('gg-shop-tag-group--handover', handover);
      group.classList.add('gg-shop-tag-group--open');
    });
    wrap.addEventListener('pointerout', (e) => {
      if (this.#suggestionsGated()) return;
      const group = e.target.closest('.gg-shop-tag-group');
      if (!group || group.contains(e.relatedTarget)) return;
      clearTimeout(this.#subrowCloseTimer);
      // 300ms grace: a pointer aiming diagonally at a subcategory decelerates
      // near tag boundaries — 160ms closed the panel mid-aim.
      this.#subrowCloseTimer = setTimeout(closeOpen, 300);
    });
  }

  disconnectedCallback() {
    clearTimeout(this.#twTimer);
    clearTimeout(this.#subrowCloseTimer);
    // The suggestion exit holds a backstop timer AND an animationend listener;
    // both must go with the component or a row torn down mid-fade leaves them
    // armed against a detached node. Same contract as the two timers above.
    this.#cancelSuggestionExit();
    this.input?.removeEventListener('focus', this.#twPause);
    this.input?.removeEventListener('blur', this.#twResume);
    this.#stickyObserver?.disconnect();
  }

  // The lookup is position:sticky (CSS); `gg-lookup--stuck` marks "currently
  // pinned" — CSS can't sense that itself. The 1px sentinel right above the
  // component crosses the nav's sticky top line exactly when the nav pins, so
  // one IntersectionObserver with that line as rootMargin is the whole sensor.
  // Class on the component (data-skip-node-update) → survives facet morphs.
  #stickyObserver = null;

  #initSticky() {
    const sentinel = this.previousElementSibling;
    if (!sentinel?.classList.contains('gg-shop-nav-sentinel')) return;
    const stickyTop = Math.ceil(parseFloat(getComputedStyle(this).top) || 0);
    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('gg-lookup--stuck', !entry.isIntersecting);
        // Unsticking always restores visibility — the hide is a stuck-only state.
        if (entry.isIntersecting) this.classList.remove('gg-lookup--hidden');
      },
      { rootMargin: `-${stickyTop + 1}px 0px 0px 0px` }
    );
    this.#stickyObserver.observe(sentinel);

    // Scroll-direction visibility while stuck: scrolling DOWN = browsing —
    // the bar slides away and gives the products the full viewport; a
    // DELIBERATE scroll up brings it back. Directional run counters (reset on
    // reversal) give hysteresis. The reveal threshold is intentionally chunky:
    // small upward corrections happen constantly while browsing downward, and
    // a hair-trigger reveal made the bar pump in and out.
    const HIDE_AFTER = 60; // px of downward travel before the bar leaves
    const SHOW_AFTER = 120; // px of upward travel before it returns
    let lastY = window.scrollY;
    let downRun = 0;
    let upRun = 0;
    window.addEventListener(
      'scroll',
      () => {
        const y = window.scrollY;
        const delta = y - lastY;
        lastY = y;
        if (!this.classList.contains('gg-lookup--stuck') || delta === 0) return;
        if (delta > 0) {
          downRun += delta;
          upRun = 0;
          if (downRun > HIDE_AFTER) this.classList.add('gg-lookup--hidden');
        } else {
          upRun -= delta;
          downRun = 0;
          if (upRun > SHOW_AFTER) this.classList.remove('gg-lookup--hidden');
        }
      },
      { passive: true }
    );
  }

  // ── Typewriter implementation ────────────────────────────────────────────
  // Prompt source, in priority order:
  //   1. <template data-gg-search-prompts> inside .gg-shop-nav__cats-wrap — the
  //      Shop's per-collection prompts. It rides the innerHTML swap that
  //      #applyServerChrome performs on every live navigation, so it always
  //      describes the CURRENT collection.
  //   2. data-search-prompts on this component — the static fallback still used
  //      by Marken (no cats-wrap) and any page without per-collection prompts.
  #promptSource() {
    const tpl = this.querySelector('template[data-gg-search-prompts]');
    return ((tpl?.dataset.ggSearchPrompts ?? this.dataset.searchPrompts) || '').trim();
  }

  // 'reveal' (default) fades each prompt in on the gg-key-tags curve through an
  // overlay span; 'typewriter' types it character by character into the native
  // placeholder. EXACTLY ONE runs — they both drive the same visual slot, and
  // the typewriter's own #twPause/#twResume rewrite the placeholder attribute,
  // so running both would have them overwrite each other.
  #revealMode() {
    return (this.dataset.placeholderStyle || 'reveal') !== 'typewriter';
  }

  #promptEl() {
    return this.querySelector('[data-gg-prompt]');
  }

  #startTypewriter() {
    const raw = this.#promptSource();
    if (!raw) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#twPrompts = raw.split('|').map((s) => s.trim()).filter(Boolean);
    if (this.#twPrompts.length === 0) return;

    this.#twStaticPlaceholder = this.input.getAttribute('placeholder') || '';
    this.input.addEventListener('focus', this.#twPause);
    this.input.addEventListener('blur', this.#twResume);

    this.#twIndex = 0;
    if (this.#revealMode() && this.#promptEl()) {
      // Hand the visual slot to the overlay: blank the native placeholder so
      // the two never show at once. #twStaticPlaceholder still holds the real
      // one, so focus/blur and the typewriter fallback restore it correctly.
      this.input.setAttribute('placeholder', '');
      this.#revealStep();
      return;
    }
    this.#twStep(this.#twPrompts[0], 0, 'typing');
  }

  // One prompt per cycle: fade in, hold, fade out, advance. The entrance is the
  // gg-key-tags reveal (opacity + rise + de-blur on that block's curve), which
  // reads far softer than character-typing at this size.
  #revealStep = () => {
    const el = this.#promptEl();
    if (!el || !this.#twPrompts.length) return;
    if (!this.#twIdle()) return; // focused or has a value — wait for blur
    const HOLD = 2600;
    el.textContent = this.#twPrompts[this.#twIndex];
    el.classList.remove('gg-lookup__prompt--out');
    el.classList.add('gg-lookup__prompt--in');
    this.#twTimer = setTimeout(() => {
      // Re-check idle: the user may have focused the field during the hold, in
      // which case #twPause has already taken the overlay down and owns the
      // slot — advancing here would fight it and re-show the prompt over their
      // typing. #twResume restarts the cycle on blur.
      if (!this.#twIdle()) return;
      el.classList.remove('gg-lookup__prompt--in');
      el.classList.add('gg-lookup__prompt--out');
      this.#twIndex = (this.#twIndex + 1) % this.#twPrompts.length;
      this.#twTimer = setTimeout(this.#revealStep, 420); // let the exit finish
    }, HOLD);
  };

  // Re-read the prompts after a live navigation and restart the rotation if the
  // new collection ships a different set. Clearing the timer first is essential:
  // without it the old cycle keeps running and two timers write the same
  // placeholder attribute, interleaving characters from two different phrases.
  #restartTypewriter() {
    const raw = this.#promptSource();
    if (!raw) return;
    const next = raw.split('|').map((s) => s.trim()).filter(Boolean);
    if (!next.length || next.join('|') === this.#twPrompts.join('|')) return;
    clearTimeout(this.#twTimer);
    this.#twTimer = null;
    this.#twPrompts = next;
    this.#twIndex = 0;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Only resume if the field is idle; otherwise #twResume picks it up on blur.
    if (this.#twIdle()) this.#twStep(this.#twPrompts[0], 0, 'typing');
  }

  // Animation runs only when the field is idle (unfocused + empty); otherwise it
  // bails and waits for #twResume (on blur of an empty field) to restart.
  #twIdle() {
    return document.activeElement !== this.input && !this.input.value;
  }

  #twStep = (text, pos, phase) => {
    if (!this.#twIdle()) return;
    const TYPE = 55, ERASE = 28, HOLD = 1600, BETWEEN = 380;
    if (phase === 'typing') {
      this.input.setAttribute('placeholder', text.slice(0, pos + 1));
      if (pos + 1 < text.length) {
        this.#twTimer = setTimeout(() => this.#twStep(text, pos + 1, 'typing'), TYPE);
      } else {
        this.#twTimer = setTimeout(() => this.#twStep(text, text.length, 'erasing'), HOLD);
      }
    } else {
      this.input.setAttribute('placeholder', text.slice(0, Math.max(pos - 1, 0)));
      if (pos - 1 > 0) {
        this.#twTimer = setTimeout(() => this.#twStep(text, pos - 1, 'erasing'), ERASE);
      } else {
        this.#twIndex = (this.#twIndex + 1) % this.#twPrompts.length;
        const next = this.#twPrompts[this.#twIndex];
        this.#twTimer = setTimeout(() => this.#twStep(next, 0, 'typing'), BETWEEN);
      }
    }
  };

  #twPause = () => {
    clearTimeout(this.#twTimer);
    this.#twTimer = null;
    // Leave a clean, complete placeholder while the user is in the field.
    this.input.setAttribute('placeholder', this.#twStaticPlaceholder);
    // Reveal mode: drop the overlay too, or it would sit on top of what the
    // user types. The static placeholder restored above takes over the slot.
    const el = this.#promptEl();
    if (el) {
      el.classList.remove('gg-lookup__prompt--in');
      el.classList.add('gg-lookup__prompt--out');
    }
  };

  #twResume = () => {
    if (this.input.value) return; // user typed something — keep static, no rotation
    clearTimeout(this.#twTimer);
    this.#twIndex = 0;
    if (this.#revealMode() && this.#promptEl()) {
      this.input.setAttribute('placeholder', ''); // hand the slot back to the overlay
      this.#revealStep();
      return;
    }
    this.#twStep(this.#twPrompts[0], 0, 'typing');
  };

  // ── Category list: active toggle + all-products redirect (was #onPillClick) ──
  // Supports both the new .gg-shop-nav__cat and the legacy .gg-lookup__pill.
  #onCategoryClick(e) {
    // Subcollection links live in the same nav — same live transition.
    const sub = e.target.closest('.gg-shop-nav__subcat');
    if (sub && sub.href) {
      this.#liveNavigate(sub.href, e, sub.textContent.trim());
      return;
    }

    const cat = e.target.closest('.gg-shop-nav__cat, .gg-lookup__pill');
    if (!cat) return;

    const isNew = cat.classList.contains('gg-shop-nav__cat');
    const itemSel = isNew ? '.gg-shop-nav__cat' : '.gg-lookup__pill';
    const activeCls = isNew ? 'gg-shop-nav__cat--active' : 'gg-lookup__pill--active';
    const stubCls = isNew ? 'gg-shop-nav__cat--stub' : 'gg-lookup__pill--stub';
    const nav = cat.closest('.gg-shop-nav__kategorien, .gg-lookup__pills');
    const isStub = cat.classList.contains(stubCls);

    if (isStub) e.preventDefault();

    // Deselect-to-all fires ONLY when the clicked category IS the current
    // collection (compare resolved pathnames). On a SUBcollection page the PARENT
    // main is highlighted (--active) but its href points at the parent collection,
    // so a class-based check would wrongly send it to /all — the path check sends
    // it to the parent instead. Stubs (href="#") never deselect.
    let onThisCollection = false;
    if (!isStub) {
      try {
        onThisCollection = new URL(cat.href, location.origin).pathname === location.pathname;
      } catch (_) { /* missing/malformed href → treat as not-current */ }
    }

    if (onThisCollection) {
      e.preventDefault();
      nav.querySelectorAll(itemSel).forEach(c => c.classList.remove(activeCls));
      // Deselect-to-all is a scope transition like any other — live (Phase B).
      this.#liveNavigate(nav.dataset.allUrl, null);
      return;
    }

    // Instant visual feedback; #applyServerChrome overwrites with server truth.
    nav.querySelectorAll(itemSel).forEach(c => c.classList.toggle(activeCls, c === cat));

    // LIVE navigation (Phase B): same-template collection links render in
    // place. #liveNavigate preventDefaults ONLY when it takes over — stubs,
    // modified clicks, Marken, the theme editor and cross-origin links keep
    // their native behavior.
    if (!isStub && isNew && cat.href) {
      this.#liveNavigate(cat.href, e, cat.textContent.trim());
    }
  }

  // ── Divider tabs — Marken only now (the Shop renders no divider since bean
  // wuhq killed the docked card): the Suche tab focuses the always-visible field. ──
  #onTabClick(e) {
    const tab = e.target.closest('.gg-shop-nav__tab');
    if (tab?.dataset.tab === 'suche') this.input?.focus();
  }

  // Filter open/closed — ONE state, two projections. The canonical class
  // `gg-lookup--filters-open` lives on this component (data-skip-node-update, so
  // it survives facet morphs); the external inline panel (#gg-filter-panel,
  // Shop only — sits between the collection header and the grid) mirrors it as
  // `is-open`, which CSS turns into the 0fr→1fr height expansion. No JS
  // animation.
  #setFiltersOpen(open) {
    if (open === this.classList.contains('gg-lookup--filters-open')) return;
    this.classList.toggle('gg-lookup--filters-open', open);
    this.#syncFilterPanel(open);

    // Persist so the chosen view carries across collection-switch navigations.
    try {
      sessionStorage.setItem(GgLookupComponent.#FILTERS_OPEN_KEY, open ? '1' : '0');
    } catch (_) { /* storage unavailable — degrade to non-persistent */ }
  }

  // Project the open state onto the external panel + the header toggle button.
  // Callable on its own because both nodes can be re-rendered out from under us
  // (#applyServerChrome innerHTML-swaps the collection header on every live nav,
  // resetting the server-rendered aria-expanded="false").
  #syncFilterPanel(open) {
    document.getElementById('gg-filter-panel')?.classList.toggle('is-open', open);
    document
      .querySelector('[data-gg-filter-toggle]')
      ?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  #onInput(e) {
    this.#renderResults(e.target.value.trim());
  }

  // ── Filter suggestions ────────────────────────────────────────────────────
  // Typing never applies a filter; it offers the facet values the query names.
  // Rendered on EVERY keystroke, undebounced: ranking is a linear scan of ~460
  // short strings (tens of microseconds), so the tag is on screen before the
  // 300ms search even fires — deliberately the moment auto-tagging used to
  // hijack the query.
  #suggestionsBox() {
    return this.querySelector('[data-gg-suggestions]');
  }

  // ── Suggestion keyboard navigation ────────────────────────────────────────
  // Index of the highlighted tag, or -1 for "none". -1 is the RESTING state and
  // it matters: Enter only applies a filter once the shopper has deliberately
  // pressed ↓/↑. Typing and hitting Enter therefore still means "search this",
  // exactly as before — nothing applies a filter without an explicit aim, which
  // is the whole point of the rebuild.
  #activeIndex = -1;

  #suggestionTags() {
    return [...(this.#suggestionsBox()?.querySelectorAll('[data-gg-suggestion]') || [])];
  }

  // Move the virtual cursor. Focus never leaves the input (that is the combobox
  // pattern): the highlight is a class plus aria-activedescendant, so the
  // shopper can keep typing with a tag highlighted and the row re-ranks under
  // them. Wraps at both ends, and steps off the ends back to -1 so ↑ from the
  // first tag returns to the plain "search what I typed" state.
  #moveActive(delta) {
    const tags = this.#suggestionTags();
    if (tags.length === 0) return;
    const next = this.#activeIndex + delta;
    // -1 → last (↑ from resting opens at the bottom); past the end → back to -1.
    this.#setActive(next < -1 ? tags.length - 1 : next >= tags.length ? -1 : next);
  }

  #setActive(index) {
    const tags = this.#suggestionTags();
    this.#activeIndex = index;
    tags.forEach((tag, i) => {
      const on = i === index;
      tag.classList.toggle('gg-shop-tag--active', on);
      tag.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const active = index >= 0 ? tags[index] : null;
    if (active) this.input.setAttribute('aria-activedescendant', active.id);
    else this.input.removeAttribute('aria-activedescendant');
    // Keep the highlighted tag in view: the row scrolls horizontally on mobile.
    active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  // True while the suggestion row owns the strip the category subrow also uses.
  // Keyed on the ROW being up, not on focus alone: focus with an empty field
  // shows no suggestions, and the categories should still be hoverable then.
  #suggestionsGated() {
    const box = this.#suggestionsBox();
    return Boolean(box) && !box.hidden;
  }

  // Facet values already applied are not offered again — their chip says so,
  // and THE CHIPS ARE THE SOURCE, not location.search.
  //
  // A COLLECTION chip (Hand & Fuss, Körper…) is rendered because we are ON that
  // collection's PAGE: its constraint lives on the node as data-chip-param /
  // data-chip-value and NEVER appears in location.search. Reading the URL alone
  // therefore missed it, and the active category leaked straight back into the
  // suggestion row — visible only in the category+term combo, since plain facet
  // chips ARE url params and excluded fine. #syncChipsFromURL already skips
  // collection chips for exactly this reason (they are not URL-derived).
  //
  // #chips() also drops chips mid-exit (--out), so a just-removed filter becomes
  // suggestable again immediately rather than after its animation.
  #activeFilterValues() {
    const out = [];
    for (const chip of this.#chips()) {
      const { chipParam: param, chipValue: value } = chip.dataset;
      if (param && value) out.push({ param, value });
    }
    // Belt-and-braces: any filter param in the URL with no chip yet (e.g. mid
    // navigation, before #syncChipsFromURL runs) still counts as active.
    for (const [param, value] of new URLSearchParams(location.search)) {
      if (!GgLookupComponent.#isFilterParam(param)) continue;
      if (out.some((o) => o.param === param && o.value === value)) continue;
      out.push({ param, value });
    }
    return out;
  }

  #renderSuggestions(query) {
    const box = this.#suggestionsBox();
    if (!box) return; // Marken and any other host without the markup
    const tpl = this.querySelector('template[data-gg-suggestion-template]');
    if (!tpl) return;

    // Reopening mid-fade (blur, then straight back in) must cancel the exit
    // before anything reads the box: survivors are reused by key below, and a
    // node still carrying --out would be re-appended still fading.
    this.#cancelSuggestionExit();

    // Touch keeps its ONE SCROLLING LINE: a swipe is native there and the
    // taller tap targets eat the width, so 6 is what fits above the fold with
    // the keyboard up.
    //
    // A pointer gets TWO WRAPPED LINES instead. The old desktop cap of 4 was a
    // workaround for a scroller a mouse cannot scroll — it hid options this row
    // exists to show. Wrapping removes the reason for it, and costs no layout:
    // cats-wrap already reserves two tag rows for its hover subrow. 8 is what
    // those two lines hold at real label widths; past that the query is too
    // broad for a tag row to be the answer anyway.
    const limit = window.matchMedia('(pointer: coarse)').matches ? 6 : 8;
    const items = this.#vocabulary.suggest(query, {
      limit,
      exclude: this.#activeFilterValues(),
    });

    if (items.length === 0) {
      this.#clearSuggestions();
      return;
    }

    // RECONCILE by target, never rebuild. This row re-ranks on EVERY keystroke,
    // and a wholesale rebuild made each surviving tag a brand-new node — so
    // gg-float-in (600ms, staggered) restarted on all of them ~5 times a second
    // while typing. Nothing ever finished; the row read as permanently
    // half-arrived. That was the "clunky" feel, and it was architectural: the
    // entrance animation is designed for a one-shot appearance (a chip, a hover
    // panel), so anything that re-runs it per keystroke is using it wrong.
    //
    // Typing "bron" → "bronz" → "bronze" keeps Bronze and Bronzer in the list
    // throughout. They now KEEP their nodes and simply move; only genuinely new
    // targets animate in. The animation is untouched — what changed is how often
    // it is asked to run.
    const keyed = new Map(
      [...box.children].map((node) => [`${node.dataset.param}=${node.dataset.value}`, node])
    );
    const wanted = items.map((item, i) => {
      const id = `${item.param}=${item.value}`;
      const existing = keyed.get(id);
      if (existing) {
        keyed.delete(id); // claimed — whatever is left over is stale
        // The id encodes position for aria-activedescendant, so it has to
        // follow the tag to its new slot even though the node is reused.
        existing.id = `gg-suggestion-${i}`;
        return existing;
      }
      return this.#suggestionNode(tpl, item, i);
    });
    keyed.forEach((node) => node.remove());
    // Re-append in rank order. appendChild MOVES an existing node rather than
    // cloning it, so a survivor keeps its running (or finished) animation and
    // does not restart — reordering is free.
    wanted.forEach((node) => box.appendChild(node));
    // The highlight CANNOT survive a re-rank. Index 1 was "Bronzer" a keystroke
    // ago and is something else now, so keeping it would make Enter apply a
    // filter the shopper never aimed at — precisely the mis-fire this rebuild
    // exists to remove. Every new character returns to the resting state.
    this.#setActive(-1);
    const wasUp = !box.hidden;
    box.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    // Only on the transition down→up. Re-ranking on every keystroke must not
    // replay this, or an already-closed subrow would restart its close on each
    // character (the row strobing the plan warns about).
    if (!wasUp) this.#closeSubrows?.();
  }

  // An exit in flight: { onEnd, box, timer }, or null. Held on the instance so a
  // refocus mid-fade can cancel it — the same cancellable-close shape
  // #initSubrowHover uses per group, and for the same reason: without it the old
  // listener and backstop stay armed and finish the close on a row that has
  // since reopened.
  #suggestionExit = null;

  #cancelSuggestionExit() {
    const p = this.#suggestionExit;
    if (!p) return;
    clearTimeout(p.timer);
    p.box.removeEventListener('animationend', p.onEnd);
    this.#suggestionExit = null;
    // Reused nodes must not come back still carrying the exit: #renderSuggestions
    // keys survivors by target and re-appends them, so a stale --out would leave
    // a tag stuck mid-fade in a row that is up.
    [...p.box.children].forEach((t) => t.classList.remove('gg-shop-tag--out'));
  }

  // `animate` is opt-IN, and only focusout passes it. A suggestion click closes
  // the row in the same frame as the filter handoff (#onSuggestionClick calls
  // this and then #applyFilter), so fading here would leave the row painted over
  // the incoming filtered grid. Escape and the zero-match path are likewise
  // instant — they are dismissals and re-ranks, not departures.
  #clearSuggestions({ animate = false } = {}) {
    const box = this.#suggestionsBox();
    if (!box) return;
    this.#cancelSuggestionExit();
    // ARIA and the highlight go down IMMEDIATELY, whatever is still painted: the
    // row stops being navigable the moment we decide to close it. Reset the
    // index DIRECTLY rather than via #setActive — the tags are gone or on their
    // way out, so #setActive would iterate them and never clear
    // aria-activedescendant, leaving the input pointing at a removed node.
    this.#activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.input.setAttribute('aria-expanded', 'false');

    const tags = [...box.children];
    const done = () => {
      this.#suggestionExit = null;
      box.hidden = true;
      box.textContent = '';
    };
    if (
      !animate ||
      box.hidden ||
      tags.length === 0 ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      done();
      return;
    }

    // `hidden` is what the :has() rules key on, so it flips LAST — flipping it
    // first would bring the category pills back over tags still animating.
    tags.forEach((t) => t.classList.add('gg-shop-tag--out'));
    // animationend BUBBLES, and every tag also carries gg-float-in — whose end
    // would otherwise finish the close early. Match the exit by NAME. (The
    // subrow close hit exactly this; see #initSubrowHover.) The backstop timer
    // calls onEnd with no event, because animationend is lost if the tab hides.
    const onEnd = (e) => {
      if (!e || e.animationName === 'gg-float-out') done();
    };
    box.addEventListener('animationend', onEnd);
    this.#suggestionExit = {
      onEnd,
      box,
      timer: setTimeout(() => onEnd(null), 400), // > the 300ms exit
    };
  }

  // One suggestion tag. The typing row and the no-match lead-in both use this,
  // so a tag is literally the same object in both places — same template, same
  // dataset contract (#onSuggestionClick reads param/value/label off it), same
  // staggered entrance. They were briefly two identical copies of this loop;
  // one factory is what keeps them from drifting apart.
  #suggestionNode(tpl, item, i, { listbox = true } = {}) {
    const node = tpl.content.firstElementChild.cloneNode(true);
    node.dataset.param = item.param;
    node.dataset.value = item.value;
    node.dataset.label = item.label;
    if (listbox) {
      // Only the typing row is a listbox. The id is what aria-activedescendant
      // points at, so ↑/↓ can move a virtual cursor while focus stays in the
      // input and the shopper keeps typing.
      node.id = `gg-suggestion-${i}`;
      // Out of the tab order: in a combobox the arrows navigate the options and
      // Tab LEAVES the widget. Tabbable options would mean tabbing through up
      // to six tags to reach the submit button, and a focused option would take
      // focus off the input and stop the shopper typing.
      node.tabIndex = -1;
    } else {
      // The no-match lead-in reuses this template but is NOT a listbox — it is a
      // couple of buttons in prose. role="option" outside a listbox is invalid
      // ARIA and would have a screen reader announce a list that isn't there.
      node.removeAttribute('role');
    }
    // Drives the same staggered entrance the category subrow tags use.
    node.style.setProperty('--gg-i', String(i));
    const label = node.querySelector('.gg-shop-tag__label');
    const facet = node.querySelector('.gg-shop-tag__facet');
    if (label) label.textContent = item.label;
    // The facet name IS the disambiguation: "Creme · Farbe" tells the shopper it
    // is a makeup shade, not a face cream. Without it the list is guesswork with
    // extra steps.
    if (facet) facet.textContent = item.facetLabel;
    return node;
  }

  // Query of the note currently on screen, or null when there is none (a real
  // match). Lets focusout add the lead-in without re-deriving the whole note.
  #lastNoteQuery = null;

  // Add the lead-in to a note that is already on screen, once the typing row is
  // down. Only ever ADDS: if a lead-in is already there, or there is no note,
  // this does nothing — so it is safe to call on every focusout.
  #repaintResultNote() {
    const note = document.querySelector('.gg-lookup__result-note');
    if (!note || !this.#lastNoteQuery) return;
    this.#prependFilterLead(note, this.#lastNoteQuery);
  }

  // Build the "no results, but there IS a filter" lead-in for the no-match note.
  // No-op when the query names nothing (the ordinary empty state stands) or when
  // the host has no suggestion template (Marken) — both silent by design.
  //
  // Clones the SAME template the typing row uses, so a lead-in tag is the same
  // object with the same styling and the same click contract; the delegated
  // handler is bound on the note itself because this node lives outside the
  // suggestions box.
  #prependFilterLead(note, query) {
    const tpl = this.querySelector('template[data-gg-suggestion-template]');
    if (!tpl || !query) return;
    // Idempotent — #repaintResultNote calls this on every focusout.
    if (note.querySelector('.gg-lookup__result-lead')) return;
    // The typing row is showing these very tags right now, so a lead-in would be
    // the same answer twice on one screen. The row is the live one and wins while
    // the field has focus; #repaintResultNote adds this the moment it goes down.
    if (this.#suggestionsGated()) return;
    const items = this.#vocabulary.suggest(query, {
      limit: 3, // a lead-in is an answer, not a menu — the row above is the menu
      exclude: this.#activeFilterValues(),
    });
    if (items.length === 0) return;

    const lead = document.createElement('div');
    lead.className = 'gg-lookup__result-lead';

    const msg = document.createElement('p');
    msg.className = 'gg-lookup__result-lead-msg';
    msg.textContent = (this.dataset.filterLeadText || '').replace('@@Q@@', query);
    lead.append(msg);

    const row = document.createElement('div');
    row.className = 'gg-lookup__result-lead-tags';
    items.forEach((item, i) => row.append(this.#suggestionNode(tpl, item, i, { listbox: false })));
    lead.append(row);

    row.addEventListener('click', (e) => {
      const tag = e.target.closest('[data-gg-suggestion]');
      if (tag) this.#onSuggestionClick(tag);
    });

    note.prepend(lead);
  }

  // Stop a search from landing on top of the filter the user just clicked.
  // TWO states, and covering only one of them is the race: a fetch already in
  // flight (abort it) AND a debounced search still queued in #searchTimer,
  // which would otherwise fire ~300ms LATER — after the handoff — and repaint
  // the grid facets had already filtered. Suggestions are undebounced, so a
  // click genuinely can land inside that window.
  #cancelPendingSearch() {
    clearTimeout(this.#searchTimer);
    this.#abortController?.abort();
    this.#teardownSearchPaging();
    this.#setBusy(false);
  }

  // A click is the ONLY thing that applies a filter. Token lifting is the same
  // as the old keyword path: the typed query becomes the chip and the field
  // clears, so the search and the filter never stack up on one another.
  #onSuggestionClick(tag) {
    const { param, value, label } = tag.dataset;
    if (!param || !value) return;
    this.#cancelPendingSearch();
    this.#clearSuggestions();
    // #applyFilter clears the input and sets owner 'facets' (terminal handoff).
    this.#applyFilter(param, value, label || value);
  }

  // Pause the results-list infinite scroll so it stops appending collection pages
  // while we show search results.
  #pauseInfiniteScroll() {
    this.#resultsList?.infinityScrollObserver?.disconnect();
  }

  // Resume infinite scroll after restoring the original grid: re-observe the
  // freshly-restored "view more" sentinels with the existing observer.
  #resumeInfiniteScroll() {
    const obs = this.#resultsList?.infinityScrollObserver;
    if (!obs || !this.#resultsList?.refs) return;
    const { viewMorePrevious, viewMoreNext } = this.#resultsList.refs;
    if (viewMorePrevious) obs.observe(viewMorePrevious);
    if (viewMoreNext) obs.observe(viewMoreNext);
  }

  // Update the header product-count to reflect the active search (the total number
  // of matches, not just the loaded page). Captures the original collection count
  // once so an emptied query can restore it.
  #setCount(n) {
    const el = this.#countEl();
    if (!el) return;
    if (this.#originalCount === null) this.#originalCount = el.textContent;
    el.textContent = n;
    this.#replayCount(el);
  }

  #restoreCount() {
    const el = this.#countEl();
    if (el && this.#originalCount !== null) {
      el.textContent = this.#originalCount;
      this.#originalCount = null;
      this.#replayCount(el);
    }
  }

  // Re-run the count's reveal so a changed result total is SEEN. The header
  // updates through two mechanisms that behave differently:
  //   • #applyServerChrome innerHTML-swaps .gg-collection-header, so the count
  //     arrives as a brand-new node and its CSS animation plays by itself.
  //   • #setCount/#restoreCount mutate textContent in place — same node, so
  //     nothing replays without forcing it.
  // Hence the remove → reflow → re-add: without the forced reflow the removal
  // and re-add coalesce into no style change at all and the animation is skipped.
  //
  // Only the COUNT animates, never the whole divider label: the label holds the
  // collection title as a bare text node (see the gg_header_label capture in
  // main-collection.liquid), so it cannot be excluded by any selector — and the
  // count changes on every keystroke, which would strobe the 24px desktop title.
  #lastCountText = null;

  #replayCount(el) {
    // Guard the no-op: a re-render that produces identical text must not
    // flicker, and #transitionToURL deliberately re-asserts the search count
    // right after a chrome swap — without this that would animate twice.
    if (el.textContent === this.#lastCountText) return;
    this.#lastCountText = el.textContent;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.classList.remove('gg-collection-header__count--in');
    void el.offsetWidth;
    el.classList.add('gg-collection-header__count--in');
  }

  // browse=true: empty query but the state changed (a chip was removed) —
  // fetch the browse grid for the CURRENT URL instead of restoring a cache.
  async #renderResults(query, browse = false) {
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    this.#teardownSearchPaging();

    const grid = this.#gridEl();
    if (!grid) return false;

    if (!query && !browse) {
      // Cleared search → back to browse mode. Drop ?q= from the URL, then
      // restore the cached grid ONLY if it was captured under this exact URL —
      // if chips/facets changed meanwhile, the cache is stale and the browse
      // grid is fetched fresh instead (no ghost filters, ever).
      const u = new URL(location.href);
      u.searchParams.delete('q');
      history.replaceState(history.state, '', `${u.pathname}${u.search}`);
      if (this.#originalGridHTML !== null && this.#originalGridURL === this.#browseUrl().href) {
        grid.innerHTML = this.#originalGridHTML;
        this.#animateGridEntrance(grid);
        this.#originalGridHTML = null;
        this.#originalGridURL = null;
        this.#setOwner('server');
        this.#setResultNote('match');
        this.#setQueryEcho('');
        this.#resumeInfiniteScroll();
        this.#restoreCount();
        return true;
      }
      this.#originalGridHTML = null;
      this.#originalGridURL = null;
      browse = true;
    }

    // A SEARCH IS ALWAYS A SEARCH. This is where a typed facet keyword used to
    // become a chip before the fetch ever ran (Phase A / sf87) — the auto-tagging
    // that guessed which value the shopper meant mid-word. The query now goes to
    // the search, whole and unmodified; any facet value it names is OFFERED by
    // #renderSuggestions, and only a click applies one.
    //
    // Baselines are only captured FROM browse mode (a search grid is never a
    // baseline), stamped with the browse-normalized URL they belong to (q/page
    // stripped — a hydrated ?q= load still captures a valid browse grid).
    if (query && this.#owner === 'server') {
      this.#originalGridHTML = grid.innerHTML;
      this.#originalGridURL = this.#browseUrl().href;
    }
    this.#pauseInfiniteScroll();

    this.#setOwner('lookup', { busy: true });

    // Scope to the current collection with a real FACET param (server-side
    // intersection) — NOT an `AND tag:` search term. The term form is a loose
    // relevance signal (inconsistent matches) AND locale-broken (the tag facet is
    // default-language only → 0 on /en). The facet `filter.p.m.product_data.collection`
    // reads a metafield Shopify syncs to collection membership nightly: a hard
    // filter, translatable, exact 1:1 with the collection. The query stays CLEAN.
    const searchQuery = query;

    try {
      const { kind, grid: gridHTML, lastPage, count, doc } = await this.#fetchSearchPage(
        searchQuery,
        1,
        this.#abortController.signal
      );
      // A zero-result search used to SILENTLY CONVERT to a filter here when the
      // typed word named a facet value — the shopper asked for "Glas" and got a
      // filtered grid without being told the search itself found nothing. That
      // net is gone with auto-tagging. The suggestion row is the replacement and
      // is already on screen for exactly these queries (it ranks the same
      // vocabulary, undebounced, from the first keystroke) — step 6 makes the
      // no-match state lead with it rather than merely leaving it visible above.
      //
      // SCOPE-AWARE BESTSELLER suggestions: a no-match suggests the current
      // scope's BESTSELLERS (collection URLs sort natively via
      // ?sort_by=best-selling; unscoped = store-wide bestsellers on /all).
      // One extra fetch, only on the rare no-match path. Fallback chain if it
      // fails: the pre-search baseline grid (the scoped browse view, already
      // captured, zero fetch) → the server's generic empty-state set.
      let cards = gridHTML;
      if (kind === 'fallback') {
        try {
          const bu = this.#browseUrl();
          bu.searchParams.set('sort_by', 'best-selling');
          const bres = await fetch(bu.toString(), { signal: this.#abortController.signal });
          if (bres.ok) {
            const best = this.#extractGrid(await bres.text());
            if (best.kind === 'match' && best.grid) cards = best.grid;
          }
        } catch (bestErr) {
          if (bestErr.name === 'AbortError') throw bestErr; // superseded — bail out
        }
        if (
          cards === gridHTML &&
          this.#chips().length &&
          this.#originalGridHTML !== null &&
          this.#originalGridURL === this.#browseUrl().href
        ) {
          cards = this.#originalGridHTML;
        }
      }
      // ONE entrance for every grid swap — search and browse alike.
      //
      // Browse used to take a `document.startViewTransition(paint)` branch
      // instead of the card cascade, on the assumption that its crossfade
      // "already communicates the change". It does not: that call passes NO
      // transition types, so `:root:active-view-transition-type(product-grid)`
      // in base.css never matches, nothing gets a view-transition-name, and with
      // `:root { view-transition-name: none }` there is nothing captured at all.
      // The result was a hard swap with no animation — which is why clicking a
      // category looked worse than searching.
      //
      // So both paths now run the same gg-cards-in cascade. One code path, one
      // motion, and the reduced-motion guard already lives in that keyframe's
      // CSS (main-collection.liquid) rather than being re-tested here.
      const liveGrid = this.#gridEl() ?? grid; // re-read: a morph may have replaced the node
      liveGrid.innerHTML = cards; // fallback keeps its cards; empty = ''
      this.#animateGridEntrance(liveGrid);
      // No-match presentation (message / suggestions) above the grid.
      this.#setResultNote(kind, query, doc);
      if (!query) {
        // Browse: nav + header + facets + chips sync from the fetched doc.
        this.#applyServerChrome(doc);
        this.#setQueryEcho('');
      }

      if (query) {
        // Search mode: this component owns the grid. Project the query into
        // the URL so reload/share reproduces exactly this state (?q= hydration
        // in connectedCallback picks it up).
        this.#owner = 'lookup'; // busy stays until the finally-block settles it
        const u = new URL(location.href);
        u.searchParams.set('q', query);
        history.replaceState(history.state, '', `${u.pathname}${u.search}`);
        // True match total in the header (0 on no-match) + the query echo.
        this.#setCount(kind === 'match' ? count : 0);
        this.#setQueryEcho(query);
        this.#announce(
          document.querySelector('.gg-collection-header')?.textContent.replace(/\s+/g, ' ').trim() || ''
        );
      } else {
        // Browse fetch: the fetched page IS the server truth for the current
        // URL — the swapped grid is the new baseline (nothing to restore).
        this.#owner = 'server'; // busy stays until the finally-block settles it
        this.#originalGridHTML = null;
        this.#originalGridURL = null;
        this.#originalCount = null;
      }

      // Own pagination ONLY for real matches (fallback suggestions are page 1
      // by design — paging them would make them read like results). Browse
      // pages re-enter the browse route via searchQuery ''.
      if (kind === 'match') {
        this.#searchQuery = searchQuery;
        this.#searchPage = 1;
        this.#searchLastPage = lastPage;
        if (lastPage > 1) this.#setupSearchPaging();
      }
      return true;
    } catch (err) {
      // Aborts = superseded by a newer render — not a failure (true keeps
      // #liveNavigate from force-navigating away from the newer state).
      if (err.name === 'AbortError') return true;
      // Network/HTTP failure → restore original rather than leave a blank grid.
      if (this.#originalGridHTML !== null) (this.#gridEl() ?? grid).innerHTML = this.#originalGridHTML;
      this.#setResultNote('match');
      this.#setQueryEcho('');
      this.#restoreCount();
      return false;
    } finally {
      // ONLY the newest render may settle busy. A superseded render's finally
      // runs on the microtask queue AFTER the render that aborted it has already
      // set busy — clearing it here would drop the skeleton while the newer
      // fetch is still in flight. The controller identity is the generation
      // token: if it's been replaced, this render is stale and settles nothing.
      if (this.#abortController === controller) this.#setBusy(false);
    }
  }

  // Rebuild the typeable-filter vocabulary from its <template> inside
  // .gg-shop-nav__cats-wrap. Called on every chrome swap because the template
  // rides in with the cats-wrap innerHTML and each collection has its own facet
  // values — a root attribute went stale after live navs.
  //
  // This method used to also build #filterMap and feed a matcher that DECIDED
  // which filter a query meant. Parsing and ranking now live in
  // FilterVocabulary, which only ever returns a ranked list; deciding is the
  // shopper's click. Missing template (Marken) = empty vocabulary = no
  // suggestions, which is the correct silent degradation.
  #parseFilterMap() {
    this.#vocabulary = FilterVocabulary.parse(this.querySelector('template[data-gg-filter-map]'));
  }


  // Apply one facet value: the chip appears immediately (token lifting — the
  // input clears) and the facets machinery renders the filtered grid
  // server-true. Returns false when the facets component isn't available.
  //
  // TERMINAL HANDOFF: from here the grid belongs to facets, whose render we
  // cannot await (see #owner). We set owner 'facets' and clear busy at the
  // handoff rather than pretending to track a fetch we can never see finish.
  #applyFilter(param, value, label = value) {
    const facetsForm = document.querySelector('facets-form-component');
    if (typeof facetsForm?.updateFiltersByURL !== 'function') return false;
    this.#setQueryEcho('');
    const already = new URLSearchParams(location.search).getAll(param).includes(value);
    if (already) {
      // The filter is already on. #renderSuggestions excludes active values, so
      // this is now only reachable by a stale click (the row re-ranked between
      // the pointerdown and the click) — kept because token-lifting still clears
      // the input, and without feedback the query would simply vanish. Pulse the
      // live chip so the no-op reads as "already applied".
      this.input.value = '';
      this.#pulseChip(param, value);
      this.#setBusy(false);
      return true;
    }
    // TOKEN LIFT — the query BECOMES the chip, so both must happen in the same
    // frame. The chip used to be created later, by the #syncChipsFromURL that
    // runs on filter:update inside updateFiltersByURL; that left the query gone
    // and no chip yet — a visible hole where the shopper's term had vanished.
    // Patching the chip in here is the same pre-render technique
    // #patchCollectionChip already uses for category clicks: instant feedback
    // now, server truth reconciled over it later (#reconcileChipsFromDoc matches
    // by param+value and updates in place, so this node is NOT re-animated).
    this.input.value = '';
    this.#patchFilterChip(param, value, label);
    const u = this.#browseUrl();
    u.searchParams.append(param, value);
    const target = `${u.pathname}${u.search}`;
    this.#setOwner('facets');

    // The handoff is IMMEDIATE. It used to wait ~260ms (GG_CHIP_ENTRANCE_MS)
    // and prefetch the section concurrently (#warmSection), to stop cold-apply
    // jank from eating the chip's entrance animation. Both existed because a
    // chip appeared that the shopper had not asked for — auto-tagging made it
    // out of their half-typed word, so the entrance was the only thing telling
    // them what had happened, and it had to be protected. A click needs no such
    // protection: the shopper aimed at the tag and knows what they did, and
    // delaying the grid behind an animation would be plain lag.
    facetsForm.updateFiltersByURL(target);
    return true;
  }


  // Insert a facet chip immediately, before the URL carries it. The idempotent
  // #syncChipsFromURL / #reconcileChipsFromDoc passes that follow both match on
  // param+value and update matches IN PLACE, so this node survives them and its
  // entrance animation is never restarted (the "chip appears, then flashes
  // through its entrance again" bug those methods exist to prevent).
  // `label` is the human-readable facet label. It differs from `value` only for
  // the taxonomy facet (filter.p.t.category), whose value is an internal id
  // ("hb-3-2-9-2") — showing that on the chip would be nonsense. Defaults to
  // value so every existing caller is unchanged.
  #patchFilterChip(param, value, label = value) {
    const box = this.querySelector('[data-gg-chips]');
    const tpl = box?.querySelector('template[data-gg-chip-template]');
    if (!box || !tpl) return;

    // Prefer the facet panel's own label for this exact param+value: the server's
    // display string, correctly cased ("Concealer"), where the filter-map keyword
    // is downcased for matching.
    //
    // NOT CSS.escape: it escapes for IDENTIFIER context, but these values sit in
    // a QUOTED attribute selector, so it injects literal backslashes (matching
    // nothing) and can throw — that throw propagated out of here and aborted
    // #applyFilter, which is why NO filter applied at all. A quoted selector
    // string only needs backslash and double-quote escaped, and the whole lookup
    // is wrapped: a cosmetic label must never cost the user their filter.
    try {
      const esc = (v) => String(v).replace(/["\\]/g, '\\$&');
      const facetInput = document.querySelector(
        `input[name="${esc(param)}"][value="${esc(value)}"]`
      );
      const facetLabel = facetInput?.dataset.label || facetInput?.getAttribute('aria-label');
      if (facetLabel) label = facetLabel;
    } catch (err) {
      /* fall back to the passed label */
    }
    const exists = [...box.querySelectorAll('.gg-lookup__chip:not(.gg-lookup__chip--out)')].some(
      (c) => c.dataset.chipParam === param && c.dataset.chipValue === value
    );
    if (exists) return;
    const chip = tpl.content.firstElementChild.cloneNode(true);
    chip.dataset.chipKind = param === 'filter.p.vendor' ? 'vendor' : 'filter';
    chip.dataset.chipParam = param;
    chip.dataset.chipValue = value;
    const labelEl = chip.querySelector('.gg-lookup__chip-label');
    if (labelEl) labelEl.textContent = label;
    box.insertBefore(chip, tpl);
  }

  // Re-run a chip's entrance animation in place, to acknowledge a no-op.
  // Same remove → reflow → re-add dance the header count uses: without the
  // forced reflow the class removal and re-add coalesce into no change at all.
  #pulseChip(param, value) {
    const chip = this.#chips().find(
      (c) => c.dataset.chipParam === param && c.dataset.chipValue === value
    );
    if (!chip || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    chip.classList.remove('gg-lookup__chip--pulse');
    void chip.offsetWidth;
    chip.classList.add('gg-lookup__chip--pulse');
    chip.addEventListener(
      'animationend',
      () => chip.classList.remove('gg-lookup__chip--pulse'),
      { once: true }
    );
  }

  // Fetch one /search results page and return { grid, lastPage }.
  //   grid     — the .product-grid innerHTML, or '' for a confirmed no-match.
  //   lastPage — total pages (from the grid's data-last-page), 1 if absent.
  //
  // We fetch the real /search page — NO predictive suggest.json gate. /search is
  // the single source of truth: fuzzy (typo-tolerant) and searches the description
  // (body), both of which the suggest API is stricter about, so a suggest gate
  // produced false negatives (e.g. "serm" or ingredient terms → 0 from suggest
  // while /search had hundreds).
  //
  // We fetch the FULL /search page (not section_id rendering). The Section
  // Rendering API does NOT resolve product.featured_media or the nested
  // _product-card-gallery block in isolated renders — verified on BOTH the search
  // and collection templates — so a section render returns image-LESS cards. The
  // full page renders media correctly. It's ~825 KB (we keep only .product-grid),
  // but correctness beats the size win. (Verified live 2026-06-18.) The page
  // supports &page=N for pagination and carries data-last-page.
  async #fetchSearchPage(searchQuery, page, signal) {
    // BROWSE ROUTE (empty query): a chip was removed with an empty input — fetch
    // the wider grid for the remaining visible chips. Collection pages return
    // the same .product-grid with data-last-page/data-results-count, so extraction
    // and paging work unchanged (the paging append re-enters here with '' too).
    if (!searchQuery) {
      const burl = this.#browseUrl();
      if (page > 1) burl.searchParams.set('page', page);
      const bres = await fetch(burl.toString(), { signal });
      if (!bres.ok) throw new Error(`browse ${bres.status}`);
      return this.#extractGrid(await bres.text());
    }

    // Auto-correct misspelled condition terms so we search the CANONICAL spelling
    // (e.g. "Cuperose" → "Couperose") and surface the right products instead of
    // letting semantic search broaden the typo into the wrong category.
    searchQuery = ggCorrectConditionQuery(searchQuery);

    // The legacy FILTER-KEYWORD ROUTE stood here: an instant-tier keyword was
    // served from the FILTER instead of the search index. It is deleted with the
    // rest of auto-tagging — it was the same guess one layer down, and it fired
    // on paging too, so page 2 of a search could silently become a filtered
    // collection page. Metafield values still cannot be free-text found; the
    // suggestion row is how a shopper reaches them now, deliberately.
    //
    // LOCALE-AWARE search path. A hardcoded '/search' always hits the ROOT
    // (default-locale) search index — so on the EN storefront (/en/…) it queried
    // the German index and returned the wrong/short result set (e.g. "reindex"
    // 625 vs the true 3380). Build the path from Shopify's locale root instead:
    // '/' → '/search', '/en/' → '/en/search'. Mirrors the form's
    // `routes.search_url`, so the JS fetch and the native form agree per locale.
    const localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const searchPath = `${localeRoot.replace(/\/?$/, '/')}search`;
    const url = new URL(searchPath, location.origin);
    url.searchParams.set('q', searchQuery);
    url.searchParams.set('type', 'product');
    // Every visible chip contributes its facet param (bare keys, NOT [] brackets
    // — Shopify ignores the bracketed form on /search). The collection chip's
    // value is the collection TITLE, exactly what the product_data.collection
    // metafield stores. NB: filter.p.vendor is default-locale-only on /search
    // (correct on DE, silently unscoped on /en) — the metafield facet is locale-safe.
    for (const c of this.#chips()) {
      url.searchParams.set(c.dataset.chipParam, c.dataset.chipValue);
    }
    if (page > 1) url.searchParams.set('page', page);

    const res = await fetch(url.toString(), { signal });
    // Throw on a bad response so the caller restores the original grid
    // (distinct from a confirmed no-match, which returns grid: '').
    if (!res.ok) throw new Error(`search ${res.status}`);
    return this.#extractGrid(await res.text());
  }

  // Lift the .product-grid out of a fetched /search section. Returns
  // { grid: innerHTML | '', lastPage: number }.
  //
  // CRITICAL no-match detection: on a true no-match, search-results.liquid swaps to
  // the empty_state_collection and FILLS .product-grid with fallback products — so
  // counting items is NOT a no-match signal (verified: nonsense → 50 fallback
  // cards). The authoritative, locale-independent marker is [data-gg-no-results],
  // a hidden element search-results.liquid emits ONLY in the no-match branch
  // specifically for this contract (so it can't be removed by an unrelated
  // cleanup). `.main-collection-grid__title` is kept as a defensive secondary
  // signal (the no-match branch also passes a `title` to product-grid), but it is
  // incidental — do not depend on it alone. (Note: an older comment claimed
  // `.search-results__no-results` was a marker here; it never appeared in the
  // search-results template gg-lookup fetches — that class lives only in the
  // predictive-search modal — so it was never a real signal.)
  #extractGrid(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // No-match is signalled ONLY by the intentional [data-gg-no-results] marker
    // search-results.liquid emits in its empty/guard branches. (Dropped the old
    // secondary check on .main-collection-grid__title — a landmine: the match
    // branch may legitimately render a title, which would then false-flag a full
    // grid as a no-match and blank real results.)
    const isNoMatch = doc.querySelector('[data-gg-no-results]');
    const grid = doc.querySelector('.product-grid');
    const hasItems = !!grid?.querySelector('.product-grid__item');
    if (isNoMatch) {
      // Two server no-match shapes (search-results.liquid):
      //   'fallback' — marker + a grid of recommendation products (generic
      //                no-match). Shown as SUGGESTIONS under an honest message.
      //   'empty'    — marker + no items (condition guard / true empty). The
      //                doc carries the honest empty-state markup to lift.
      if (hasItems) {
        return { kind: 'fallback', grid: grid.innerHTML, lastPage: 1, count: 0, doc };
      }
      return { kind: 'empty', grid: '', lastPage: 1, count: 0, doc };
    }
    if (!hasItems) {
      return { kind: 'empty', grid: '', lastPage: 1, count: 0, doc };
    }
    const lastPage = parseInt(grid.getAttribute('data-last-page'), 10) || 1;
    const count = parseInt(grid.getAttribute('data-results-count'), 10) || 0;
    return { kind: 'match', grid: grid.innerHTML, lastPage, count, doc };
  }

  // ── Search-results own pagination ──────────────────────────────────────────
  // A sentinel after the grid, watched by a dedicated observer; on intersect we
  // append the next /search page. Independent of the (paused) collection scroll.
  #setupSearchPaging() {
    const grid = this.#gridEl();
    if (!grid) return;
    this.#searchSentinel = document.createElement('div');
    this.#searchSentinel.className = 'gg-lookup__search-sentinel';
    this.#searchSentinel.setAttribute('aria-hidden', 'true');
    grid.after(this.#searchSentinel);

    this.#searchObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) this.#appendNextSearchPage();
      },
      { rootMargin: '600px' } // prefetch before it scrolls into view
    );
    this.#searchObserver.observe(this.#searchSentinel);
  }

  #teardownSearchPaging() {
    this.#searchObserver?.disconnect();
    this.#searchObserver = null;
    this.#searchSentinel?.remove();
    this.#searchSentinel = null;
    this.#searchLoading = false;
    this.#searchQuery = '';
    this.#searchPage = 1;
    this.#searchLastPage = 1;
  }

  async #appendNextSearchPage() {
    if (this.#searchLoading) return;
    if (this.#searchPage >= this.#searchLastPage) {
      this.#teardownSearchPaging(); // exhausted — stop observing
      return;
    }
    this.#searchLoading = true;
    const nextPage = this.#searchPage + 1;
    try {
      const { grid: gridHTML } = await this.#fetchSearchPage(
        this.#searchQuery,
        nextPage,
        this.#abortController.signal
      );
      const grid = this.#gridEl();
      if (!grid || gridHTML === '') {
        this.#teardownSearchPaging();
        return;
      }
      // Append only the new cards (parse the fragment, move its <li>s in).
      const frag = new DOMParser().parseFromString(`<ul>${gridHTML}</ul>`, 'text/html');
      const items = frag.querySelectorAll('.product-grid__item');
      if (items.length === 0) {
        this.#teardownSearchPaging();
        return;
      }
      items.forEach((li) => grid.appendChild(li));
      // Cascade the appended batch (staggered relative to itself).
      this.#animateGridEntrance(grid, grid.children.length - items.length);
      this.#searchPage = nextPage;
      // Keep the sentinel after the freshly grown grid and RE-OBSERVE it: an
      // IntersectionObserver won't re-fire while an element stays continuously
      // intersecting, so if the sentinel is still on-screen after appending we'd
      // stall. unobserve→observe forces a fresh intersection check, which loads
      // the next page if it's still visible (until the grid grows past viewport
      // or pages are exhausted).
      if (this.#searchSentinel && this.#searchObserver) {
        grid.after(this.#searchSentinel);
        this.#searchObserver.unobserve(this.#searchSentinel);
        this.#searchObserver.observe(this.#searchSentinel);
      }
    } catch (err) {
      if (err.name !== 'AbortError') this.#teardownSearchPaging();
    } finally {
      this.#searchLoading = false;
    }
  }
}

// ─────────────────────────────────────────────────────────────────
// PHASE C — Inline Predictive Search dropdown  (bean: greenglam-thestore-glke)
// ─────────────────────────────────────────────────────────────────
// Extend #onInput to also show a predictive dropdown above the grid.
// GET /search/suggest.json?q={query}&resources[type]=product&resources[limit]=6
// Render product thumbnails + titles in a .gg-lookup__results overlay below the input.
//
// PHASE E — AI Advisory Layer  (bean: greenglam-thestore-glkg)
// ─────────────────────────────────────────────────────────────────
// Activate: ADD data-ai-endpoint="/apps/gg-lookup/search" data-ai-enabled="true"
// to <gg-lookup-component> plus an answer container (the placeholder markup was
// stripped 2026-07-06 — parked attrs/divs shipped on every page for nothing).
// Intercept submit for advisory queries (skin conditions / ingredients / questions).
// POST endpoint → { answer, employee: { name, role, avatar_url }, products: [] }
// Render the answer, then re-run #filterProducts with the tag filter.

if (!customElements.get('gg-lookup-component')) {
  customElements.define('gg-lookup-component', GgLookupComponent);
}
